/**
 * AtomicSimulator — pure-functional transition logic for the perfect-play solver.
 *
 * Each public function takes a {@link SolverState} + current hash and returns a
 * new state + hash (or null if the action is illegal). Input state is never
 * mutated. The solver's DFS keeps the previous state alive for backtracking,
 * so these functions must produce fresh allocations for every mutated field.
 *
 * Scope (stage 2): atomic transitions only. No search, no transposition table,
 * no dead-end detection — those live in stage 3.
 *
 * ORDERED-FLOW MODEL (rev. 2026-09-03 — age-bounded, no bursting)
 * ---------------------------------------------------------------
 * The normative rules live in **`docs/ordered-flow-semantics.md`**, and the
 * live game runs them through `src/systems/OrderedFlowEngine.ts`. This module
 * is the solver's implementation of the same rulebook; where the two ever
 * disagree the ENGINE is the reference and this file is the bug (the binding
 * cross-test in `__tests__/ordered-flow-binding.test.ts` is what enforces it).
 *
 * Two actions:
 *
 *   `dispatch(attackerId)` — legal ONLY when the in-flight ring has room
 *     (`ringSlotsUsed + slotCount <= conveyorCap`). In order:
 *     1. age bound: if the ring's FRONT rider has `age >= conveyorCap`, its
 *        forced arrival fires first. If that arrival does not fit the stack
 *        the run is LOST at the arrival and the dispatch does not happen
 *        (the lap never runs);
 *     2. pop the attacker from its queue lanes or from the stack;
 *     3. resolve its whole lap (`resolveLap`);
 *     4. every rider still in the ring ages by 1 (this was a player action);
 *     5. land it: every orb spent → gone (and frozen queue counters tick);
 *        ammo left → appended to the TAIL of the in-flight ring at age 0;
 *     6. auto-trigger any hammer newly exposed at a queue front.
 *
 *   `settle` — the ring's oldest rider arrives on the stack, then the riders
 *     left behind age by 1. Legal exactly when the ring is non-empty. A settle
 *     chosen while the front is at the age bound IS that forced arrival — it
 *     discharges the obligation; nothing arrives twice.
 *
 * The stack constraint binds at ARRIVAL, and the win — zero cubes — is
 * immediate. AGE is the model's clock: a rider gets "up to `conveyorCap` time
 * steps of grace" (Arthur's ruling) on the belt, counted in player actions
 * since its own dispatch resolved; once the front's age reaches the cap, the
 * next player action is preceded by its arrival. That defer window is strictly
 * within live-play reality (a lap lasts seconds; C taps fit easily), which is
 * what makes every model trace executable in the live game by pacing.
 *
 * Three retired models, so none comes back: the *parking pen* (a survivor sat
 * on the belt until the player scheduled a free `advance` — 10 buffer slots via
 * a mechanic the game never had); *forced advance* (`fa81b6b1`: a survivor
 * went straight to the stack in the same step as its lap — 5 buffer slots when
 * the game has 10); and the *burst* model (2026-09-01→03: a tap into a full
 * belt forced "belt-pressure" arrivals instead of being refused — Arthur
 * playtested it, lost to a mid-lap forced eviction, and rejected the
 * mechanic). Under the current rules a full belt REFUSES the dispatch, exactly
 * as the live game refuses the tap.
 *
 * Firing semantics mirror `BotStrategy.simulateLapWithUndo` +
 * `ShootingSystem.firePerpendicularShot`:
 *   - Perimeter firing order = `buildFiringSequence(rows, cols)`.
 *   - One shot per firing line per lap per attacker.
 *   - Lead-orb-first: iterate `attacker.orbs`; the first orb with matching
 *     color for this line's first-visible target fires. Break on first hit.
 *   - Blockade walls block shots from their edge; a matching-color orb can
 *     damage the wall (consuming 1 ammo), non-matching shots are blocked entirely.
 *   - Doors are transparent (shots pass through); they lose HP when any
 *     matching-color cube clears.
 *   - Mega blocks: each hit decrements mega HP; when 0, all footprint cells clear.
 *   - Snakes: shots fired "down" column return null if first non-door cell
 *     is a snake cell; shots firing "up" a column hit the bottom-most snake cell.
 *   - Barricades: cells are invisible to shooting until their HP reaches 0 via
 *     hammer triggers (hammers fire the lowest-id barricade still alive).
 */

import { firingLineKey, type FiringLineDirection } from '../firingLineKey';
import {
  applyWallHpChange,
  applyBarricadeHpChange,
  applyHammerConsumed,
  attackerHash,
  canonicalizeStack,
  getBarricadeSalt,
  getCellColorHash,
  getConveyorHash,
  getDoorSalt,
  getMegaSalt,
  getQueueLaneHash,
  getStackHash,
  getWallSalt,
  hashState,
  mixHp,
  type DispatchAction,
  type SolverAction,
  type SolverAttacker,
  type SolverState,
} from './SolverState';

// ── Constants / helpers ─────────────────────────────────────────────────────

type Direction = FiringLineDirection;

interface FiringLine {
  direction: Direction;
  lineIndex: number;
}

/**
 * Produce the canonical perimeter firing sequence. Matches
 * `BotStrategy.buildFiringSequence` exactly.
 *
 *   Bottom (left→right, firing UP)
 *   Right  (bottom→top, firing LEFT)
 *   Top    (right→left, firing DOWN)
 *   Left   (top→bottom, firing RIGHT)
 */
const firingSeqCache = new Map<number, FiringLine[]>();
function buildFiringSequence(rows: number, cols: number): FiringLine[] {
  // Cache key: rows << 12 | cols. Board dimensions are fixed per level; the
  // cache is populated once per (rows, cols) pair across all solver runs.
  const key = (rows << 12) | cols;
  const cached = firingSeqCache.get(key);
  if (cached !== undefined) return cached;
  const seq: FiringLine[] = [];
  for (let c = 0; c < cols; c++) seq.push({ direction: 'up', lineIndex: c });
  for (let r = rows - 1; r >= 0; r--) seq.push({ direction: 'left', lineIndex: r });
  for (let c = cols - 1; c >= 0; c--) seq.push({ direction: 'down', lineIndex: c });
  for (let r = 0; r < rows; r++) seq.push({ direction: 'right', lineIndex: r });
  firingSeqCache.set(key, seq);
  return seq;
}

/**
 * Line-key encoding: direction code * LINE_STRIDE + lineIndex.
 * Shared with GameScene and the other lap engines via `firingLineKey` so the
 * live game and this model cannot drift apart.
 */
const lineKey = firingLineKey;

// ── First-visible cache ─────────────────────────────────────────────────────
//
// `firstVisible(state, direction, lineIndex)` depends ONLY on board content,
// barricade HP, door cells (immutable meta), and snake cells (immutable meta).
// Wall HP does NOT affect `firstVisible` — walls are consulted separately by
// `findBlockingWall` inside `simulateAtomicLap`. Mega HP does not affect
// `firstVisible` either: when mega HP hits 0 the cells get cleared (board
// mutation, already in boardHash); while HP > 0 the mega's color lives in
// the board array which is likewise in boardHash.
//
// So `boardHash` (cells + barricade HP) is the correct cache key for
// `firstVisible` — two states that share the same boardHash produce
// identical `firstVisible` results for every (direction, lineIndex) pair.
//
// Empirical motivation: on cute levels the solver visits 50K-90K unique
// states with the same board content (queue/conveyor/stack vary, board
// only mutates when a cube clears). The reach-colors cache already exploits
// this for 4*W scans per state, but `simulateAtomicLap` does another 4*W
// `firstVisible` calls PER DISPATCH, dominating per-dispatch cost. Sharing
// results across dispatches that land on the same board is the lever.

/**
 * Result of {@link firstVisible}. `hit` is the target cell (or null when
 * the scan finds nothing). The "no-target" case is explicitly encoded as
 * `hit: null` so cache hits can distinguish "absent" from "cached null".
 */
export interface FirstVisibleResult {
  hit: { row: number; col: number; color: number; isSnake: boolean } | null;
}

/**
 * Cache for `firstVisible(state, direction, lineIndex)` keyed on
 * (boardHash, lineKey(direction, lineIndex)). Two distinct search states
 * that share the same board content share entries here.
 *
 * Populated lazily: a miss scans the line and stores the result; subsequent
 * visits (same board, same line) return the stored value in O(1).
 *
 * Key insight: `simulateAtomicLap` mutates the board in-place as hits land,
 * so the boardHash changes mid-lap. The first scan against a newly-mutated
 * board is a miss; subsequent visits of ANY future lap against that same
 * board (e.g. through a transposition loop where the queue/conveyor/stack
 * differ but the board is the same) get cached hits for free.
 */
export class FirstVisibleCache {
  private readonly map = new Map<bigint, Map<number, FirstVisibleResult>>();
  hits = 0;
  misses = 0;
  private readonly capacity: number;

  constructor(capacity = 500_000) {
    this.capacity = capacity;
  }

  /** Look up the cached result for this (boardHash, direction, lineIndex). */
  get(boardHash: bigint, lineKey: number): FirstVisibleResult | undefined {
    const inner = this.map.get(boardHash);
    if (inner === undefined) return undefined;
    const res = inner.get(lineKey);
    if (res !== undefined) this.hits++;
    return res;
  }

  /** Store the result for this (boardHash, direction, lineIndex). */
  set(boardHash: bigint, lineKey: number, value: FirstVisibleResult): void {
    this.misses++;
    if (this.map.size >= this.capacity) {
      // Simple overflow policy: drop everything. Rehydration is cheap
      // (O(W) per missed line), so LRU bookkeeping isn't worth the overhead.
      this.map.clear();
    }
    let inner = this.map.get(boardHash);
    if (inner === undefined) {
      inner = new Map();
      this.map.set(boardHash, inner);
    }
    inner.set(lineKey, value);
  }

  get size(): number {
    return this.map.size;
  }
}

/** Get the wall side that would block a shot firing in the given direction. */
function wallSideForDirection(direction: Direction): 'top' | 'bottom' | 'left' | 'right' {
  switch (direction) {
    case 'right': return 'left';
    case 'left':  return 'right';
    case 'down':  return 'top';
    case 'up':    return 'bottom';
  }
}

/**
 * Board cell a blockade-wall hit should animate towards — the edge cell of the
 * line the shot was fired along. Mirrors `ShootingSystem.getWallEdgeTarget`;
 * used only when a {@link LapShotRecord} is being collected.
 */
function wallEdgeTarget(
  state: SolverState,
  side: 'top' | 'bottom' | 'left' | 'right',
  cellIndex: number,
): { row: number; col: number } {
  switch (side) {
    case 'left':   return { row: cellIndex, col: 0 };
    case 'right':  return { row: cellIndex, col: state.boardWidth - 1 };
    case 'top':    return { row: 0, col: cellIndex };
    case 'bottom': return { row: state.boardHeight - 1, col: cellIndex };
  }
}

/**
 * The line's cell index = row for horizontal directions, col for vertical.
 * Returns the first active wall blocking this line from the relevant edge,
 * or null.
 */
function findBlockingWall(
  state: SolverState,
  direction: Direction,
  lineIndex: number,
): { wallId: number; colorIndex: number } | null {
  const side = wallSideForDirection(direction);
  for (const idStr of Object.keys(state.wallHp)) {
    const id = Number(idStr);
    const info = state.meta.wallInfoById[id];
    if (!info) continue;
    if (info.side !== side) continue;
    if (lineIndex >= info.startCell && lineIndex < info.startCell + info.length) {
      return { wallId: id, colorIndex: info.colorIndex };
    }
  }
  return null;
}

/**
 * Compute the color of the first cell visible on a firing line, or null if
 * the line is empty / blocked.
 *
 * - Skips door cells and barricade cells (both are transparent / invisible).
 * - Mega footprint cells report the mega's color.
 * - Snake cells block `right`/`left`/`up` firing (return null) — matches
 *   `ShootingSystem.findPerpendicularTarget` and `BotStrategy.findTarget`.
 * - Snake cells on `down` firing return the color with `isSnake=true`; the
 *   caller resolves the hit to the bottom-most snake cell in the column
 *   (per `ShootingSystem.firePerpendicularShot` + `BoardSystem.removeSnakeBottomCube`).
 *
 * Snake semantics alignment history: a previous version of this function
 * drilled 'up'+snake to the bottom-most snake cell — internally consistent
 * but not replayable against the real runtime. The strict form was
 * reinstated with a snake-aware closure tightening (see
 * {@link DeadEndDetector.computeClosure} comment about per-column snake
 * reachability) that keeps Snake Pillars inside the 60s tier-2 budget.
 */
function firstVisibleUncached(
  state: SolverState,
  direction: Direction,
  lineIndex: number,
): { row: number; col: number; color: number; isSnake: boolean } | null {
  const { board, boardWidth, boardHeight, meta } = state;
  const doorCells = meta.doorCells;
  const snakeCells = meta.snakeCells;

  const scan = (rStart: number, rStep: number, cStart: number, cStep: number, rEnd: number, cEnd: number) => {
    let r = rStart, c = cStart;
    while ((rStep === 0 || (rStep > 0 ? r < rEnd : r >= 0)) &&
           (cStep === 0 || (cStep > 0 ? c < cEnd : c >= 0))) {
      const idx = r * boardWidth + c;
      const isBarricade = (state.barricadeHp[idx] ?? 0) > 0;
      if (doorCells.has(idx) || isBarricade) {
        r += rStep; c += cStep;
        continue;
      }
      const v = board[idx];
      if (v === 0) {
        r += rStep; c += cStep;
        continue;
      }
      const color = v - 1;
      const snake = snakeCells.has(idx);
      return { row: r, col: c, color, isSnake: snake };
    }
    return null;
  };

  if (direction === 'right') {
    const r = lineIndex;
    const res = scan(r, 0, 0, 1, r + 1, boardWidth);
    if (!res) return null;
    if (res.isSnake) return null; // snake blocks right
    return res;
  } else if (direction === 'left') {
    const r = lineIndex;
    const res = scan(r, 0, boardWidth - 1, -1, r + 1, -1);
    if (!res) return null;
    if (res.isSnake) return null;
    return res;
  } else if (direction === 'down') {
    const c = lineIndex;
    const res = scan(0, 1, c, 0, boardHeight, c + 1);
    if (!res) return null;
    // 'down' is the one direction where snake cells DO produce a valid
    // first-visible target (mirrors ShootingSystem.findPerpendicularTarget
    // + BotStrategy.findTarget — both return the (row,col), and the caller
    // resolves the removal to the bottom-most snake cell).
    return res;
  } else { // up
    const c = lineIndex;
    const res = scan(boardHeight - 1, -1, c, 0, -1, c + 1);
    if (!res) return null;
    // Snakes BLOCK 'up' firing (matches ShootingSystem.findPerpendicularTarget
    // line ~89 + BotStrategy.findTarget line ~592 — both return null when
    // the first-visible is a snake). A shot fired up a column that hits a
    // snake as its first-visible target is absorbed — no cube cleared, no
    // ammo consumed.
    if (res.isSnake) return null;
    return res;
  }
}

/**
 * Compute the color of the first cell visible on a firing line, or null if
 * the line is empty / blocked. See {@link firstVisibleUncached} for the
 * un-cached core; this wrapper consults an optional `FirstVisibleCache`
 * keyed on the state's `boardHash`.
 *
 * When `cache` is provided:
 *   - Cache hit: returns the stored result directly, skipping the O(W)
 *     line scan.
 *   - Cache miss: runs the scan, stores the wrapped result, returns it.
 *
 * Soundness: `firstVisible`'s output depends only on board cells, barricade
 * HP, door cells (immutable meta), and snake cells (immutable meta). The
 * state's `boardHash` covers cells + barricade HP, which is exactly the
 * set of inputs that vary across search states. Door / wall / mega HP do
 * NOT affect the output (see `FirstVisibleCache` class doc).
 */
function firstVisible(
  state: SolverState,
  direction: Direction,
  lineIndex: number,
  cache?: FirstVisibleCache,
  boardHash?: bigint,
): { row: number; col: number; color: number; isSnake: boolean } | null {
  if (cache !== undefined && boardHash !== undefined) {
    const key = lineKey(direction, lineIndex);
    const cached = cache.get(boardHash, key);
    if (cached !== undefined) return cached.hit;
    const hit = firstVisibleUncached(state, direction, lineIndex);
    cache.set(boardHash, key, { hit });
    return hit;
  }
  return firstVisibleUncached(state, direction, lineIndex);
}

/** Walk downward from (startRow, col) along contiguous snake cells. */
function findSnakeBottomRow(state: SolverState, startRow: number, col: number): number {
  const { boardWidth, boardHeight, meta } = state;
  let bottomRow = startRow;
  for (let r = startRow + 1; r < boardHeight; r++) {
    const idx = r * boardWidth + col;
    if (meta.snakeCells.has(idx) && state.board[idx] !== 0) bottomRow = r;
    else break;
  }
  return bottomRow;
}

// ── Lap trace (shared with the ordered-flow engine) ────────────────────────
//
// The solver never looks at these types. They exist so `OrderedFlowEngine` —
// the live game's logical layer, see `docs/ordered-flow-semantics.md` — can
// resolve a lap through THIS code and then animate the result. Re-deriving a
// kill list anywhere else would be a second model, which is exactly the
// divergence this whole design removes. The engine owns dispatch/arrival
// bookkeeping itself (its rules differ from `applyDispatch`'s); the one thing
// it borrows is {@link resolveLap}.
//
// Collecting is opt-in and allocation-free when omitted: every solver call site
// passes `undefined` and pays one `if` per shot.

/** What one shot of a lap did, in the order the lap took it. */
export interface LapShotRecord {
  /** Perimeter firing line this shot was taken from. */
  direction: Direction;
  lineIndex: number;
  /** `firingLineKey(direction, lineIndex)` — the shared dedup key. */
  lineKey: number;
  /** Which of the attacker's orbs fired (index into `attacker.orbs`). */
  orbIndex: number;
  colorIndex: number;
  kind: 'cube' | 'snake' | 'mega' | 'wall';
  /** First-visible cell the shot was aimed at (the projectile's target). */
  target: { row: number; col: number };
  /**
   * Cell actually cleared. Equal to `target` except for snakes, where the
   * bottom-most snake cell in the column dies instead. Absent for mega hits
   * that did not destroy the block and for wall hits.
   */
  removal?: { row: number; col: number };
  megaId?: number;
  megaHpAfter?: number;
  /** Footprint cells cleared when a mega's HP reached 0. */
  megaClearedCells?: { row: number; col: number }[];
  wallId?: number;
  wallHpAfter?: number;
  wallDestroyed?: boolean;
}

/** A hammer that auto-triggered off a queue front after the dispatch's pop. */
export interface HammerTriggerRecord {
  attackerId: number;
  /** Barricade struck, or null when none was left alive. */
  barricadeId: number | null;
}

// ── Lap simulation ─────────────────────────────────────────────────────────

/**
 * Run one full lap for `attacker` over the board — returns a new state +
 * hash + post-lap attacker. The input `state` and `attacker` are NOT mutated
 * (the function makes fresh copies of the mutating fields up front).
 *
 * Optimization: the lap mutates a working copy of the board + HP records
 * in-place as hits land, then wraps the mutated buffers into a new state at
 * the end. Hash is maintained incrementally via direct XOR using the
 * exported Zobrist primitives. This replaces the old implementation, which
 * created a fresh Uint16Array + state object per hit (up to one allocation
 * per cube cleared per lap) — a dominant cost on snake-heavy levels.
 */
function simulateAtomicLap(
  state: SolverState,
  hash: bigint,
  attacker: SolverAttacker,
  firstVisibleCache?: FirstVisibleCache,
  shots?: LapShotRecord[],
): { state: SolverState; hash: bigint; postAttacker: SolverAttacker } {
  // Work on a mutable copy of the attacker's orbs.
  const orbs = attacker.orbs.map(o => ({ color: o.color, ammo: o.ammo }));

  // Fresh, mutable copies of the lap-variant fields. Everything else
  // (queueLanes, conveyor, stack, meta, etc.) is reused unchanged.
  const board = new Uint16Array(state.board);
  const doorHp: Record<number, number> = { ...state.doorHp };
  const wallHp: Record<number, number> = { ...state.wallHp };
  const megaHp: Record<number, number> = { ...state.megaHp };
  let h = hash;
  // Board-only hash: incremental counterpart to the state hash, scoped to
  // cells + barricade HP so the reach-colors cache can share entries across
  // states that only differ in queue/conveyor/stack/door/wall/mega HP.
  // Cells XOR here exactly when they XOR into `h`. Barricades are NOT
  // touched inside simulateAtomicLap (they only change via hammer triggers
  // in applyHammerTriggers), so we only add a cell-delta path here.
  let bh = state.boardHash;
  const boardWidth = state.boardWidth;

  // Wrap the mutable buffers into a view we hand to the read-only helpers
  // (firstVisible / findBlockingWall), which read {board, doorHp, wallHp,
  // megaHp, meta, ...}. The queue/stack/conveyor are never read by those
  // helpers, so aliasing them to the input state is safe.
  //
  // NOTE: we intentionally KEEP the identity-stable reference to
  // state.barricadeHp. No lap step mutates barricades (hammers are the
  // only mechanism, and hammers run outside simulateAtomicLap).
  const temp: SolverState = {
    ...state,
    board,
    doorHp,
    wallHp,
    megaHp,
  };

  const seq = buildFiringSequence(state.boardHeight, state.boardWidth);
  const firedLines = new Set<number>();

  const decrementDoorsForColor = (color: number): void => {
    // Iterate all live doors; decrement matching-color ones.
    for (const idStr of Object.keys(doorHp)) {
      const id = Number(idStr);
      if (state.meta.doorColorById[id] !== color) continue;
      const cur = doorHp[id];
      if (cur <= 0) continue;
      const salt = getDoorSalt(id);
      h ^= mixHp(salt, cur);
      h ^= mixHp(salt, cur - 1);
      if (cur - 1 <= 0) delete doorHp[id];
      else doorHp[id] = cur - 1;
    }
  };

  const clearCubeInPlace = (row: number, col: number, color: number): void => {
    const idx = row * boardWidth + col;
    const oldColor = board[idx];
    if (oldColor === 0) return;
    board[idx] = 0;
    const cellDelta = getCellColorHash(idx, oldColor);
    h ^= cellDelta;
    bh ^= cellDelta;
    decrementDoorsForColor(color);
  };

  const hitMegaInPlace = (megaId: number): { hpAfter: number; cleared: { row: number; col: number }[] } => {
    const cur = megaHp[megaId] ?? 0;
    const cleared: { row: number; col: number }[] = [];
    if (cur <= 0) return { hpAfter: 0, cleared };
    const newHp = cur - 1;
    const salt = getMegaSalt(megaId);
    h ^= mixHp(salt, cur);
    h ^= mixHp(salt, newHp);
    if (newHp <= 0) {
      delete megaHp[megaId];
      const cells = state.meta.megaCellsById[megaId] ?? [];
      for (const idx of cells) {
        const v = board[idx];
        if (v === 0) continue;
        const color = v - 1;
        const row = (idx / boardWidth) | 0;
        const col = idx - row * boardWidth;
        clearCubeInPlace(row, col, color);
        if (shots !== undefined) cleared.push({ row, col });
      }
    } else {
      megaHp[megaId] = newHp;
    }
    return { hpAfter: Math.max(0, newHp), cleared };
  };

  const hitSnakeInPlace = (row: number, col: number, color: number): number => {
    // Find the bottom-most snake cell.
    let bottomRow = row;
    for (let r = row + 1; r < state.boardHeight; r++) {
      const idx = r * boardWidth + col;
      if (state.meta.snakeCells.has(idx) && board[idx] !== 0) bottomRow = r;
      else break;
    }
    clearCubeInPlace(bottomRow, col, color);
    return bottomRow;
  };

  const hitWallInPlace = (wallId: number): number => {
    const cur = wallHp[wallId] ?? 0;
    if (cur <= 0) return -1;
    const salt = getWallSalt(wallId);
    h ^= mixHp(salt, cur);
    h ^= mixHp(salt, cur - 1);
    if (cur - 1 <= 0) delete wallHp[wallId];
    else wallHp[wallId] = cur - 1;
    return cur - 1;
  };

  for (const line of seq) {
    if (orbs.every(o => o.ammo <= 0)) break;
    const key = lineKey(line.direction, line.lineIndex);
    if (firedLines.has(key)) continue;

    // Check for blocking wall on this line.
    const wall = findBlockingWall(temp, line.direction, line.lineIndex);
    if (wall) {
      for (let oi = 0; oi < orbs.length; oi++) {
        const orb = orbs[oi];
        if (orb.ammo <= 0) continue;
        if (orb.color === wall.colorIndex) {
          const hpAfter = hitWallInPlace(wall.wallId);
          if (hpAfter < 0) break;
          orb.ammo--;
          firedLines.add(key);
          if (shots !== undefined) {
            shots.push({
              direction: line.direction,
              lineIndex: line.lineIndex,
              lineKey: key,
              orbIndex: oi,
              colorIndex: orb.color,
              kind: 'wall',
              // Walls occupy no board cell; aim at the edge cell of this line,
              // matching `ShootingSystem.getWallEdgeTarget`.
              target: wallEdgeTarget(state, wallSideForDirection(line.direction), line.lineIndex),
              wallId: wall.wallId,
              wallHpAfter: hpAfter,
              wallDestroyed: hpAfter <= 0,
            });
          }
          break;
        }
      }
      continue;
    }

    // `bh` is the current in-lap board hash — covers cells + barricade HP,
    // which is the exact set of inputs `firstVisible` depends on. A miss
    // here (most often: first scan of a newly-mutated board) populates the
    // cache; subsequent laps landing on the same board (via transposition
    // revisits with different queue/conveyor/stack) hit in O(1).
    const tgt = firstVisible(temp, line.direction, line.lineIndex, firstVisibleCache, bh);
    if (!tgt) continue;

    for (let oi = 0; oi < orbs.length; oi++) {
      const orb = orbs[oi];
      if (orb.ammo <= 0) continue;
      if (orb.color !== tgt.color) continue;

      const cellIdx = tgt.row * boardWidth + tgt.col;
      const megaId = state.meta.megaIdByCell[cellIdx];

      const record: LapShotRecord | undefined = shots === undefined ? undefined : {
        direction: line.direction,
        lineIndex: line.lineIndex,
        lineKey: key,
        orbIndex: oi,
        colorIndex: orb.color,
        kind: 'cube',
        target: { row: tgt.row, col: tgt.col },
      };

      if (megaId !== undefined && (megaHp[megaId] ?? 0) > 0) {
        const mega = hitMegaInPlace(megaId);
        if (record) {
          record.kind = 'mega';
          record.megaId = megaId;
          record.megaHpAfter = mega.hpAfter;
          if (mega.cleared.length > 0) record.megaClearedCells = mega.cleared;
        }
      } else if (tgt.isSnake) {
        const bottomRow = hitSnakeInPlace(tgt.row, tgt.col, tgt.color);
        if (record) {
          record.kind = 'snake';
          record.removal = { row: bottomRow, col: tgt.col };
        }
      } else {
        clearCubeInPlace(tgt.row, tgt.col, tgt.color);
        if (record) record.removal = { row: tgt.row, col: tgt.col };
      }
      orb.ammo--;
      firedLines.add(key);
      if (record && shots) shots.push(record);
      break;
    }
  }

  // Build the post-lap state. Most fields alias state; the mutated buffers
  // (board, doorHp, wallHp, megaHp) are our fresh copies. `boardHash` is
  // carried by the lap-local `bh` accumulator so the reach-colors cache
  // can key on it in O(1) without re-scanning the board.
  const postState: SolverState = {
    ...state,
    board,
    doorHp,
    wallHp,
    megaHp,
    boardHash: bh,
  };

  const postAttacker: SolverAttacker = {
    id: attacker.id,
    orbs,
    slotCount: attacker.slotCount,
  };
  if (attacker.frozen !== undefined) postAttacker.frozen = attacker.frozen;
  if (attacker.isHammer) postAttacker.isHammer = attacker.isHammer;
  if (attacker.mystery) postAttacker.mystery = attacker.mystery;
  return { state: postState, hash: h, postAttacker };
}

// ── Queue helpers ─────────────────────────────────────────────────────────────

/**
 * Slots the in-flight ring occupies (sum of slotCount). Under ordered flow this
 * is live state: dispatch is legal iff `ringSlotsUsed + slotCount <= conveyorCap`.
 */
export function conveyorSlotsUsed(state: SolverState): number {
  let n = 0;
  for (const a of state.conveyor) n += a.slotCount;
  return n;
}

/** Count total stack slots used (sum of slotCount). */
export function stackSlotsUsed(state: SolverState): number {
  let n = 0;
  for (const a of state.stack) n += a.slotCount;
  return n;
}

/**
 * Remove attacker from all queue lanes that hold it (at position 0), and
 * return the XOR delta to apply to the state hash. Delta semantics:
 *   new_hash = old_hash ^ delta
 *
 * For each affected lane L (where `attacker` sits at position 0):
 *   - Remove the (L, 0, ah(attacker)) contribution.
 *   - For each subsequent position p in the lane, XOR out (L, p, ah(entries[p]))
 *     and XOR in (L, p-1, ah(entries[p])).
 *
 * O(sum of affected lane lengths). Called in hot paths (applyDispatch) to
 * avoid recomputing the full state hash after every queue mutation.
 */
function removeFromQueueWithHash(
  state: SolverState,
  attacker: SolverAttacker,
): { state: SolverState; delta: bigint } {
  let delta = 0n;
  const ah = attackerHash(attacker);
  const newLanes = state.queueLanes.map((lane, laneIdx) => {
    const idx = lane.indexOf(attacker);
    if (idx === -1) return lane;
    // Remove attacker's own contribution at its current position (= idx).
    delta ^= getQueueLaneHash(laneIdx, idx, ah);
    // Every subsequent attacker shifts down by one position.
    for (let p = idx + 1; p < lane.length; p++) {
      const ahP = attackerHash(lane[p]);
      delta ^= getQueueLaneHash(laneIdx, p, ahP);        // remove old pos
      delta ^= getQueueLaneHash(laneIdx, p - 1, ahP);    // add new pos
    }
    const next = lane.slice();
    next.splice(idx, 1);
    return next;
  });
  return { state: { ...state, queueLanes: newLanes }, delta };
}

/**
 * Remove a specific attacker (by reference) from the stack and return the
 * XOR delta. Stack is canonicalized during full-state hashing, so we cannot
 * do a simple position-shift here: instead we compute the canonical stack
 * hash contribution before and after, and XOR the difference.
 */
function removeFromStackWithHash(
  state: SolverState,
  attacker: SolverAttacker,
): { state: SolverState; delta: bigint } {
  const idx = state.stack.indexOf(attacker);
  if (idx === -1) return { state, delta: 0n };
  const oldStack = canonicalizeStack(state.stack);
  const next = state.stack.slice();
  next.splice(idx, 1);
  const newStack = canonicalizeStack(next);
  let delta = 0n;
  for (let i = 0; i < oldStack.length; i++) delta ^= getStackHash(i, attackerHash(oldStack[i]));
  for (let i = 0; i < newStack.length; i++) delta ^= getStackHash(i, attackerHash(newStack[i]));
  return { state: { ...state, stack: next }, delta };
}

/**
 * Push an arriving attacker onto the stack and return the XOR delta. The stack
 * is canonicalized during hashing (it's a multiset), so the delta is "XOR out
 * every old canonical position, XOR in every new one" rather than a single
 * slot contribution.
 *
 * Overflow is NOT rejected here: an arrival at a full stack is exactly how the
 * player loses, so we return the overflowed state and let `isLoss` / the
 * search's `stackSlotsUsed > stackCap` terminal check see it. Declining to
 * finish a lap is not a move the game offers.
 */
function pushStackWithHash(
  state: SolverState,
  attacker: SolverAttacker,
): { state: SolverState; delta: bigint } {
  const oldCanon = canonicalizeStack(state.stack);
  const newStack = [...state.stack, attacker];
  const newCanon = canonicalizeStack(newStack);
  let delta = 0n;
  for (let i = 0; i < oldCanon.length; i++) delta ^= getStackHash(i, attackerHash(oldCanon[i]));
  for (let i = 0; i < newCanon.length; i++) delta ^= getStackHash(i, attackerHash(newCanon[i]));
  return { state: { ...state, stack: newStack }, delta };
}

/**
 * Append a lap survivor to the TAIL of the in-flight ring and return the XOR
 * delta. The ring hashes POSITIONALLY (see `hashState`), and appending touches
 * exactly one position, so the delta is a single constant.
 */
function pushConveyorWithHash(
  state: SolverState,
  attacker: SolverAttacker,
): { state: SolverState; delta: bigint } {
  const pos = state.conveyor.length;
  const delta = getConveyorHash(pos, attackerHash(attacker));
  return { state: { ...state, conveyor: [...state.conveyor, attacker] }, delta };
}

/**
 * Remove the ring's OLDEST rider (`conveyor[0]`) and return it with the XOR
 * delta. Every surviving rider shifts down one position, so the delta XORs each
 * out of its old slot and into its new one — the same shape as
 * {@link removeFromQueueWithHash}. Returns `null` when the ring is empty.
 */
function shiftConveyorWithHash(
  state: SolverState,
): { state: SolverState; delta: bigint; attacker: SolverAttacker } | null {
  const rider = state.conveyor[0];
  if (rider === undefined) return null;
  let delta = getConveyorHash(0, attackerHash(rider));
  for (let p = 1; p < state.conveyor.length; p++) {
    const ah = attackerHash(state.conveyor[p]);
    delta ^= getConveyorHash(p, ah);      // out of the old position
    delta ^= getConveyorHash(p - 1, ah);  // into the new one
  }
  return { state: { ...state, conveyor: state.conveyor.slice(1) }, delta, attacker: rider };
}

/**
 * Drop the ring-only `age` field from a rider that is leaving the ring. The
 * wait is over — the stack is a multiset of attackers, and two otherwise
 * identical attackers that rode for different lengths of time must collide
 * there (and must hash as they did before ages existed).
 */
function stripAge(a: SolverAttacker): SolverAttacker {
  if (a.age === undefined) return a;
  const { age: _age, ...rest } = a;
  return rest;
}

/**
 * Is the ring's FRONT rider at the age bound? When true, the next player
 * action must be preceded by its forced arrival (`docs/ordered-flow-semantics.md`).
 * Ages strictly decrease front-to-back (each action ages every rider by the
 * same +1, and riders join one at a time), so at most one rider can be at the
 * bound and it is always the front.
 */
function frontAtAgeBound(state: SolverState): boolean {
  const front = state.conveyor[0];
  return front !== undefined && (front.age ?? 0) >= state.conveyorCap;
}

/**
 * A player action happened: every rider in the ring ages by 1, capped at
 * `conveyorCap` (the bound itself — storing beyond it changes nothing, and the
 * cap keeps the hashed age domain finite). Each aged rider is a FRESH object,
 * which keeps `attackerHash`'s identity-keyed memoisation sound. Returns the
 * XOR delta for the positional ring hash.
 */
function ageConveyorWithHash(state: SolverState): { state: SolverState; delta: bigint } {
  if (state.conveyor.length === 0) return { state, delta: 0n };
  let delta = 0n;
  const cap = state.conveyorCap;
  const conveyor = state.conveyor.map((a, pos) => {
    const oldAge = a.age ?? 0;
    const newAge = oldAge >= cap ? cap : oldAge + 1;
    if (newAge === oldAge) return a;
    const aged: SolverAttacker = { ...a, age: newAge };
    delta ^= getConveyorHash(pos, attackerHash(a));
    delta ^= getConveyorHash(pos, attackerHash(aged));
    return aged;
  });
  return { state: { ...state, conveyor }, delta };
}

/**
 * ARRIVE — `docs/ordered-flow-semantics.md` §3. Move the ring's oldest rider
 * onto the stack. This is never chosen directly; it is produced by a `settle`,
 * by the age bound (forced arrival), or by the quiescence drain.
 *
 * An arrival that does not fit is still PLACED: the returned state carries
 * `stackSlotsUsed > stackCap`, which is precisely what the loss condition
 * reads. Returns `null` only when the ring is empty. The rider's ring-only
 * `age` is stripped as it seats — see {@link stripAge}.
 */
function applyArrival(
  state: SolverState,
  hash: bigint,
): { state: SolverState; hash: bigint; attacker: SolverAttacker; overflow: boolean } | null {
  const shifted = shiftConveyorWithHash(state);
  if (shifted === null) return null;
  const settled = stripAge(shifted.attacker);
  const used = stackSlotsUsed(shifted.state);
  const overflow = used + settled.slotCount > shifted.state.stackCap;
  const pushed = pushStackWithHash(shifted.state, settled);
  return {
    state: pushed.state,
    hash: hash ^ shifted.delta ^ pushed.delta,
    attacker: settled,
    overflow,
  };
}

/** Non-hash variants (kept for callers that don't need incremental hashing). */
function removeFromQueue(state: SolverState, attacker: SolverAttacker): SolverState {
  return removeFromQueueWithHash(state, attacker).state;
}

/**
 * Recompute the full state hash. Called after queue / stack / conveyor
 * mutations, where slot-keyed Zobrist constants make incremental updates
 * complex for marginal gain (queues and stacks are short).
 */
function rehash(state: SolverState): bigint {
  return hashState(state);
}

// ── Hammer trigger ──────────────────────────────────────────────────────────

/**
 * If any queue lane's front is a hammer (at position 0 in all its lanes),
 * auto-trigger it: remove from queue, strike the lowest-id barricade with
 * HP > 0 (decrementing all its cells' HP by 1). Chain until no more hammers.
 */
export function applyHammerTriggers(
  state: SolverState,
  hash: bigint,
  triggers?: HammerTriggerRecord[],
): { state: SolverState; hash: bigint } {
  let s = state;
  let h = hash;

  while (true) {
    // Find a hammer at position 0 in every lane it occupies.
    let hammer: SolverAttacker | null = null;
    for (let l = 0; l < s.queueLanes.length; l++) {
      const front = s.queueLanes[l][0];
      if (!front || !front.isHammer) continue;
      // All lanes containing this hammer must have it at position 0.
      let ok = true;
      for (let k = 0; k < s.queueLanes.length; k++) {
        const lane = s.queueLanes[k];
        const idx = lane.indexOf(front);
        if (idx !== -1 && idx !== 0) { ok = false; break; }
      }
      if (ok) { hammer = front; break; }
    }
    if (!hammer) break;

    // Remove hammer from all its lanes.
    s = removeFromQueue(s, hammer);
    const hammerConsumed = applyHammerConsumed(s, h);
    s = hammerConsumed.state;
    h = hammerConsumed.hash;

    // Pick lowest-id barricade with any HP > 0 and decrement.
    let barricadeIdToHit: number | null = null;
    for (const id of s.meta.barricadeIds) {
      const cells = s.meta.barricadeCellsById[id] ?? [];
      let alive = false;
      for (const cell of cells) {
        if ((s.barricadeHp[cell] ?? 0) > 0) { alive = true; break; }
      }
      if (alive) { barricadeIdToHit = id; break; }
    }

    if (barricadeIdToHit !== null) {
      const cells = s.meta.barricadeCellsById[barricadeIdToHit];
      for (const cell of cells) {
        const cur = s.barricadeHp[cell] ?? 0;
        if (cur <= 0) continue;
        const res = applyBarricadeHpChange(s, cell, cur - 1, h);
        s = res.state;
        h = res.hash;
      }
    }

    if (triggers !== undefined) {
      triggers.push({ attackerId: hammer.id, barricadeId: barricadeIdToHit });
    }

    // After removing a queue front, rehash to pick up slot-dependent changes.
    h = rehash(s);
  }

  return { state: s, hash: h };
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Check whether the state is "stuck" — in-flight ring empty, stack empty, and
 * no non-frozen queue-front attacker is dispatchable. Under ordered flow the
 * ring guard genuinely binds: with a rider still on the belt a `settle` is
 * always available, so the run is not stuck and the frozen escape hatch must
 * not open. (`OrderedFlowEngine.isStuckOnFrozenFront` makes the same two
 * checks, in the same order.) Mirrors
 * `StrategicSimulator`'s force-dispatch fallback (see line 666 in
 * `StrategicSimulator.ts` — `conveyorUsed === 0 && this.stack.length === 0 &&
 * !this.queueEmpty()`), which relaxes the frozen dispatch rule so that play
 * can continue when a frozen attacker blocks all queue lanes.
 *
 * We adopt this same escape hatch in the solver so that level-audit claims
 * remain consistent between the bot sim (source of truth for winnability
 * caches / `rate=1.0`) and the perfect-play search. Without it, any seed
 * whose very first attackers produce a frozen lane-front blockade is
 * declared LOSS by the solver even though the audit sims declare it WIN.
 */
function isStuckOnFrozenFront(state: SolverState): boolean {
  if (state.conveyor.length > 0) return false;
  if (state.stack.length > 0) return false;
  const lanes = state.queueLanes;
  const numLanes = lanes.length;
  // Any non-frozen, non-hammer queue front that has consensus → not stuck.
  // Consensus fast-path: single-orb is always consensus (lives in one lane),
  // multi-orb check is a direct-reference walk over its contiguous home lanes.
  for (let l = 0; l < numLanes; l++) {
    const front = lanes[l][0];
    if (!front) continue;
    if (front.isHammer) continue;
    if ((front.frozen ?? 0) > 0) continue;
    const sc = front.slotCount;
    if (sc > 1) {
      let ok = true;
      for (let k = 1; k < sc; k++) {
        if (lanes[l + k]?.[0] !== front) { ok = false; break; }
      }
      if (!ok) continue;
    }
    // Conveyor is empty here (guard above), so capacity check simplifies.
    if (front.slotCount > state.conveyorCap) continue;
    // There IS a non-frozen dispatchable front → not stuck.
    return false;
  }
  // No non-frozen front dispatchable; if queue has ANY front, we're stuck.
  for (const lane of lanes) {
    if (lane[0]) return true;
  }
  return false;
}

/**
 * Rule toggles for the witness-triage replay paths. The search and every
 * production caller run with the defaults — the model.
 */
export interface RuleOptions {
  /**
   * Enforce the age bound (the forced arrival of a front rider whose age has
   * reached `conveyorCap`). Default TRUE — that is the model. `false` exists
   * ONLY for witness triage: archived witnesses (proved before the bound
   * existed) that fail under the full rules are re-replayed without it, and a
   * pass there classifies the failure as "died to the age bound alone" —
   * the level needs a re-proof, not an investigation. Never pass false from
   * the search.
   */
  ageBound?: boolean;
}

/**
 * DISPATCH — `docs/ordered-flow-semantics.md` §1, and the mirror of
 * `OrderedFlowEngine.dispatch`.
 *
 * Legal ONLY when the ring has room (`ringSlotsUsed + slotCount <=
 * conveyorCap`) — a full belt refuses the dispatch, exactly as the live game
 * refuses the tap. In order: fire the front rider's forced arrival if it is at
 * the age bound, pop the attacker out of its queue lanes (or the stack), run
 * one full perimeter lap against the board (updating board + door/wall/mega HP
 * incrementally), age every rider still in the ring by 1, land the attacker
 * (culled if every orb emptied — which also ticks frozen queue counters —
 * otherwise appended to the tail of the in-flight ring at age 0), and finally
 * auto-trigger any hammer newly exposed at a queue front.
 *
 * THE AGE BOUND. A rider gets up to `conveyorCap` player actions of grace on
 * the belt; when the front's age reaches the cap, this action is preceded by
 * its forced arrival. That arrival can overflow the stack, and the player is
 * allowed to lose that way — when it does, the run is over AT THAT ARRIVAL and
 * the lap never runs, so we return the overflowed state with the attacker
 * still in its lane.
 *
 * An overflowing arrival is never *refused*: the returned state carries
 * `stackSlotsUsed > stackCap`, which is what {@link isLoss} and the search's
 * terminal check read. Refusing it would hand the player an option the real
 * game never offers ("decline to finish a lap").
 *
 * Returns null if the action is illegal:
 *   - attacker not found
 *   - queue-front consensus not met (attacker not at position 0 in all its lanes)
 *   - attacker frozen (unless the state is "stuck" — see
 *     {@link isStuckOnFrozenFront}, which mirrors
 *     `StrategicSimulator.tryDispatch`'s force-dispatch fallback)
 *   - the attacker is structurally too wide for the belt (`slotCount > conveyorCap`)
 *   - the ring lacks room for it RIGHT NOW (`ringSlotsUsed + slotCount >
 *     conveyorCap`). Room is judged on the state as it stands — a pending
 *     forced arrival does not pre-count, matching the live game, where the
 *     player waits for the lap to end and taps then.
 */
export function applyDispatch(
  state: SolverState,
  hash: bigint,
  action: DispatchAction,
  firstVisibleCache?: FirstVisibleCache,
  rules?: RuleOptions,
): { state: SolverState; hash: bigint } | null {
  // Locate the attacker: queue or stack.
  let source: 'queue' | 'stack' | null = null;
  let attacker: SolverAttacker | null = null;
  let homeLane = -1;

  for (let l = 0; l < state.queueLanes.length; l++) {
    const f = state.queueLanes[l][0];
    if (f && f.id === action.attackerId) {
      attacker = f;
      source = 'queue';
      homeLane = l;
      break;
    }
  }
  if (!attacker) {
    for (const a of state.stack) {
      if (a.id === action.attackerId) {
        attacker = a;
        source = 'stack';
        break;
      }
    }
  }
  if (!attacker || !source) return null;

  // Hammers are not player-dispatchable.
  if (attacker.isHammer) return null;

  // Frozen attackers are normally not dispatchable. Exception: when the state
  // is "stuck on a frozen front" (conveyor + stack empty, no non-frozen
  // queue-front dispatchable) we allow a frozen QUEUE-FRONT attacker to
  // be force-dispatched. This mirrors StrategicSimulator's behaviour and
  // keeps solver winnability consistent with the audit's bot sim.
  if ((attacker.frozen ?? 0) > 0) {
    if (source !== 'queue') return null;
    if (!isStuckOnFrozenFront(state)) return null;
  }

  // Structural belt check — a property of the LEVEL: an attacker wider than
  // the whole belt can never ride it.
  if (attacker.slotCount > state.conveyorCap) return null;

  // Ring-room check — a property of the MOMENT: dispatch is legal iff the ring
  // has room for this attacker's slotCount right now. This is the same refusal
  // the live game gives a tap while the belt is full (the burst rule is
  // retired). Judged pre-forced-arrival, like the live game.
  if (conveyorSlotsUsed(state) + attacker.slotCount > state.conveyorCap) return null;

  // If dispatching from queue, require consensus at position 0 across every
  // lane that holds this attacker. For single-orb (slotCount===1) the
  // attacker lives in exactly ONE lane (see `packQueueLanes`) so it's
  // trivially at position 0 — the check is vacuous. For multi-orb we
  // check direct lane-front references at the contiguous home lanes
  // (homeLane..homeLane+slotCount-1), avoiding the O(lanes × depth) walk
  // `indexOf` would do.
  if (source === 'queue') {
    const sc = attacker.slotCount;
    if (sc > 1) {
      for (let k = 1; k < sc; k++) {
        if (state.queueLanes[homeLane + k]?.[0] !== attacker) return null;
      }
    }
  }

  let s: SolverState = state;
  let h = hash;

  // 1. AGE BOUND. If the ring's front rider has been deferred for the full
  //    grace window (`age >= conveyorCap`), this player action is preceded by
  //    its forced arrival. An arrival that does not fit ends the run right
  //    there — the attacker is NOT popped and its lap never runs, so we hand
  //    back the overflowed state for the terminal check to read. (Mirrors
  //    `OrderedFlowEngine.dispatch` step 1.) Ages strictly decrease
  //    front-to-back, so at most one rider can be at the bound; the loop is a
  //    guard, not an expectation.
  if (rules?.ageBound !== false) {
    while (frontAtAgeBound(s)) {
      const arrival = applyArrival(s, h);
      /* istanbul ignore next — frontAtAgeBound implies a non-empty ring */
      if (arrival === null) break;
      s = arrival.state;
      h = arrival.hash;
      if (arrival.overflow) return { state: s, hash: h };
    }
  }

  // 2. Remove from source. Update hash incrementally (avoids an O(N) rehash
  //    pass at the ~260K-states-per-search snake-heavy hot path).
  if (source === 'queue') {
    const res = removeFromQueueWithHash(s, attacker);
    s = res.state;
    h ^= res.delta;
  } else {
    const res = removeFromStackWithHash(s, attacker);
    s = res.state;
    h ^= res.delta;
  }

  // 3. Simulate one lap over the board. The lap mutates board + HP in-place
  //    and returns the hash with those deltas XOR'd in.
  const lap = simulateAtomicLap(s, h, attacker, firstVisibleCache);
  s = lap.state;
  h = lap.hash;

  // 3b. This was a player action: every rider already in the ring ages by 1.
  //     Runs BEFORE the survivor lands so the dispatched attacker itself ends
  //     the action at age 0 ("actions taken since its dispatch resolved").
  const aged = ageConveyorWithHash(s);
  s = aged.state;
  h ^= aged.delta;

  // 4. Land it.
  //
  //   - Exhausted (ALL orbs at ammo 0): the attacker evaporates the instant
  //     its last orb empties (see StrategicSimulator.exhaustAttackers, called
  //     per-line after every fireSegment) and every still-frozen QUEUE
  //     attacker's counter ticks down (StrategicSimulator.thawFrozenQueue).
  //   - Survivor (any orb with ammo left): it joins the TAIL of the in-flight
  //     ring at age 0 and rides until something arrives it — a `settle`, the
  //     age bound, or the quiescence drain. It does NOT touch the stack now;
  //     that is the whole point of ordered flow.
  const postAtk = lap.postAttacker;
  const fullyDepleted = postAtk.orbs.every(o => o.ammo <= 0);
  if (fullyDepleted) {
    // Decrement `frozen` counter on every still-frozen queue attacker. Use an
    // id-keyed map so multi-orb attackers spanning multiple lanes get the same
    // copy reference (prevents double-decrement and keeps lane entries
    // identity-equal for `indexOf` consensus checks).
    const idToNew = new Map<number, SolverAttacker>();
    const newLanes = s.queueLanes.map((lane, laneIdx) =>
      lane.map((a, p) => {
        if (!a) return a;
        if ((a.frozen ?? 0) <= 0) return a;
        if (!idToNew.has(a.id)) {
          const newFrozen = (a.frozen ?? 0) - 1;
          const copy: SolverAttacker = { ...a };
          if (newFrozen <= 0) delete copy.frozen;
          else copy.frozen = newFrozen;
          idToNew.set(a.id, copy);
        }
        const copy = idToNew.get(a.id)!;
        h ^= getQueueLaneHash(laneIdx, p, attackerHash(a));     // remove old
        h ^= getQueueLaneHash(laneIdx, p, attackerHash(copy));  // add new
        return copy;
      }),
    );
    s = { ...s, queueLanes: newLanes };
  } else {
    // The survivor joins the in-flight ring. `frozen` is deliberately dropped:
    // a dispatched attacker is never still frozen — the force-dispatch escape
    // hatch clears it on the way out, exactly like `QueueSystem.forceDispatchFrom`
    // and `OrderedFlowEngine.dispatch`. (Carrying it through would strand the
    // attacker on the stack forever, since a frozen stack attacker can't be
    // dispatched.)
    const rider: SolverAttacker = {
      id: postAtk.id,
      orbs: postAtk.orbs,
      slotCount: postAtk.slotCount,
    };
    if (postAtk.isHammer) rider.isHammer = postAtk.isHammer;
    if (postAtk.mystery) rider.mystery = postAtk.mystery;
    const pushRes = pushConveyorWithHash(s, rider);
    s = pushRes.state;
    h ^= pushRes.delta;
  }

  // 5. Auto-trigger any hammers newly exposed at queue lane fronts. The helper
  //    handles its own hash bookkeeping (including re-hashing after queue
  //    shuffles on hammer consumption — see applyHammerTriggers).
  const post = applyHammerTriggers(s, h);
  return { state: post.state, hash: post.hash };
}

/**
 * SETTLE — `docs/ordered-flow-semantics.md` §2, and the mirror of
 * `OrderedFlowEngine.settle`.
 *
 * "A lap completed with nothing else happening": the ring's oldest rider
 * arrives on the stack, and the riders left behind age by 1 (a settle is a
 * player action). Legal exactly when the ring is non-empty; returns null
 * otherwise. An arrival that does not fit is still placed, leaving
 * `stackSlotsUsed > stackCap` for the terminal check.
 *
 * In live play the clock generates settles (a lap ending IS one). In the model
 * it is the player's "I waited". No age-bound pre-step here: the settle
 * arrives exactly the rider the bound would have forced, so it discharges the
 * obligation itself — a front-at-the-bound state never arrives two riders on
 * one settle.
 */
export function applySettle(
  state: SolverState,
  hash: bigint,
): { state: SolverState; hash: bigint } | null {
  const arrival = applyArrival(state, hash);
  if (arrival === null) return null;
  const aged = ageConveyorWithHash(arrival.state);
  return { state: aged.state, hash: arrival.hash ^ aged.delta };
}

/**
 * Apply any {@link SolverAction}. This is the transition function the search
 * drives; `applyDispatch` / `applySettle` are the per-kind entry points.
 */
export function applyAction(
  state: SolverState,
  hash: bigint,
  action: SolverAction,
  firstVisibleCache?: FirstVisibleCache,
  rules?: RuleOptions,
): { state: SolverState; hash: bigint } | null {
  return action.type === 'settle'
    ? applySettle(state, hash)
    : applyDispatch(state, hash, action, firstVisibleCache, rules);
}

// ── Replaying witnesses of an earlier vintage ───────────────────────────────

/**
 * Does this witness need IMPLIED SETTLES to replay?
 *
 * A witness with no `settle` in it was proved under the strict forced-advance
 * model, where a survivor landed on the stack in the same step as its lap. That
 * is a legal ordered-flow trace only once you insert a `settle` after every
 * dispatch that produced a survivor — without them the survivor is left riding
 * the belt, and the witness's next attempt to re-dispatch it *from the stack*
 * finds nothing there.
 *
 * A witness that names its own settles is native ordered flow and must be
 * replayed verbatim; adding more would arrive riders the trace meant to keep on
 * the belt. (An `advance`-bearing witness is neither — reject it before you get
 * here; see `WitnessReplaySystem.classifyWitness`.)
 */
export function witnessNeedsImpliedSettles(witness: readonly SolverAction[]): boolean {
  return !witness.some(a => a.type === 'settle');
}

/**
 * Did `dispatch(attackerId)` leave a rider on the belt? True exactly when the
 * attacker survived its lap, since a survivor is appended to the ring's tail
 * and nothing else can be there. This is the trigger for an implied settle.
 */
export function dispatchLeftRider(state: SolverState, attackerId: number): boolean {
  const tail = state.conveyor[state.conveyor.length - 1];
  return tail !== undefined && tail.id === attackerId;
}

/**
 * Replay one witness action, inserting the implied `settle` a strict-model
 * witness needs. Pass `impliedSettle: false` (or use {@link applyAction}) for a
 * native ordered-flow trace.
 *
 * Enforces the current rules (`rules` defaults to the model — age bound on).
 * Returns null when the action is illegal — the caller reports the step.
 */
export function applyWitnessAction(
  state: SolverState,
  hash: bigint,
  action: SolverAction,
  impliedSettle: boolean,
  firstVisibleCache?: FirstVisibleCache,
  rules?: RuleOptions,
): { state: SolverState; hash: bigint } | null {
  const next = applyAction(state, hash, action, firstVisibleCache, rules);
  if (next === null) return null;
  if (!impliedSettle || action.type !== 'dispatch') return next;
  if (!dispatchLeftRider(next.state, action.attackerId)) return next;
  return applySettle(next.state, next.hash) ?? next;
}

/**
 * Everything one whole-witness model replay reports.
 */
export interface WitnessModelReplayResult {
  /** The witness reached a win under the rules it was replayed with. */
  ok: boolean;
  /** Witness actions consumed (an early win consumes fewer than the total). */
  steps: number;
  reason?: string;
  /**
   * Room-making settles inserted ahead of dispatches that lacked ring room.
   * A burst-era witness dispatched into a full belt and had the "belt
   * pressure" rule force FIFO arrivals of the oldest riders; under the
   * current rules that dispatch is refused, and the faithful translation is
   * an explicit `settle` per arrival — the SAME riders arrive at the SAME
   * points in the trace. A fresh (current-model) witness never needs one.
   */
  insertedSettles: number;
  /** Age-bound forced arrivals that fired. Always 0 with `ageBound: false`. */
  forcedArrivals: number;
  /** The state the replay ended in (win, loss, or wherever it failed). */
  state: SolverState;
}

export interface WitnessModelReplayOptions extends RuleOptions {
  /**
   * Insert an implied `settle` after every surviving dispatch (the strict
   * forced-advance vintage). Defaults by detection:
   * {@link witnessNeedsImpliedSettles}.
   */
  impliedSettle?: boolean;
  firstVisibleCache?: FirstVisibleCache;
}

/**
 * Replay a whole witness through the model, enforcing the CURRENT rules —
 * ring-room-gated dispatch and (by default) the age bound — and translating
 * the two archived vintages into legal current-model traces:
 *
 *   - strict (dispatch-only): implied `settle` after every survivor;
 *   - burst-era native: room-making settles inserted before any dispatch that
 *     no longer fits the ring (see {@link WitnessModelReplayResult.insertedSettles}).
 *
 * A mid-trace overflow — from an inserted settle, an age-forced arrival, or a
 * witness settle — is a LOSS and fails the replay with a reason naming which.
 * `ageBound: false` replays under the pre-age rulebook (burst translation
 * included), which is exactly the old model; a witness that fails with the
 * bound and passes without it died to the age bound alone and needs re-proof.
 */
export function replayWitnessThroughModel(
  state0: SolverState,
  witness: readonly SolverAction[],
  options: WitnessModelReplayOptions = {},
): WitnessModelReplayResult {
  const impliedSettle = options.impliedSettle ?? witnessNeedsImpliedSettles(witness);
  const ageBound = options.ageBound !== false;
  const rules: RuleOptions = { ageBound };
  const fvc = options.firstVisibleCache;

  let state = state0;
  let hash = hashState(state0);
  let insertedSettles = 0;
  let forcedArrivals = 0;

  const overflowed = (): boolean => stackSlotsUsed(state) > state.stackCap;
  const result = (ok: boolean, steps: number, reason?: string): WitnessModelReplayResult => ({
    ok, steps, insertedSettles, forcedArrivals, state,
    ...(reason !== undefined ? { reason } : {}),
  });

  for (let i = 0; i < witness.length; i++) {
    if (isWin(state)) return result(true, i); // early win — the rest is surplus
    const action = witness[i];

    if (action.type === 'dispatch') {
      // Burst translation: settle until the ring can take the attacker.
      const target = findDispatchTarget(state, action.attackerId);
      if (target !== null) {
        while (
          state.conveyor.length > 0
          && conveyorSlotsUsed(state) + target.slotCount > state.conveyorCap
        ) {
          const settled = applySettle(state, hash)!;
          insertedSettles++;
          state = settled.state;
          hash = settled.hash;
          if (overflowed()) {
            return result(false, i, `step ${i}: room-making settle overflowed the stack`);
          }
        }
      }
      if (ageBound && frontAtAgeBound(state)) forcedArrivals++;
      const next = applyWitnessAction(state, hash, action, impliedSettle, fvc, rules);
      if (next === null) {
        return result(false, i, `step ${i}: ${JSON.stringify(action)} returned null`);
      }
      state = next.state;
      hash = next.hash;
      if (overflowed()) {
        return result(
          false, i,
          `step ${i}: overflow on ${impliedSettle ? 'an implied settle or ' : ''}an age-forced arrival`,
        );
      }
    } else {
      const next = applySettle(state, hash);
      if (next === null) {
        return result(false, i, `step ${i}: settle with an empty in-flight ring`);
      }
      state = next.state;
      hash = next.hash;
      if (overflowed()) return result(false, i, `step ${i}: settle overflowed the stack`);
    }
  }

  if (isWin(state)) return result(true, witness.length);
  let cells = 0;
  for (let i = 0; i < state.board.length; i++) if (state.board[i] !== 0) cells++;
  return result(false, witness.length, `replay finished with ${cells} cells remaining`);
}

/**
 * Locate a witness dispatch's target the way {@link applyDispatch} will: a
 * queue-lane front or a stack member. Used by the replay translation to size
 * the room it has to make; legality itself is still applyDispatch's call.
 */
function findDispatchTarget(state: SolverState, attackerId: number): SolverAttacker | null {
  for (const lane of state.queueLanes) {
    const front = lane[0];
    if (front && front.id === attackerId) return front;
  }
  for (const a of state.stack) {
    if (a.id === attackerId) return a;
  }
  return null;
}

/**
 * Resolve ONE lap and nothing else — the shared kill-list primitive.
 *
 * `applyDispatch` above bundles a lap with the solver's own arrival rule
 * (survivor goes straight to the stack). The live game's model
 * (`docs/ordered-flow-semantics.md`) has a different arrival rule: a survivor
 * enters an in-flight ring and lands on the stack later, at its own event. So
 * `OrderedFlowEngine` borrows only this half, guaranteeing that the game and
 * the solver clear cubes by literally the same code even while their buffer
 * semantics differ.
 *
 * The attacker is neither taken from the queue nor placed anywhere: the caller
 * owns that. `shots` (optional) collects the animation script.
 */
export function resolveLap(
  state: SolverState,
  hash: bigint,
  attacker: SolverAttacker,
  firstVisibleCache?: FirstVisibleCache,
  shots?: LapShotRecord[],
): { state: SolverState; hash: bigint; postAttacker: SolverAttacker } {
  return simulateAtomicLap(state, hash, attacker, firstVisibleCache, shots);
}

/*
 * `applyAdvance` (retired 2026-09-01, and staying retired)
 * -------------------------------------------------------
 * The old `advance` was a free, player-scheduled conveyor→stack move off a
 * post-lap holding pen the game never had — a second 5-slot buffer on top of
 * the stack. {@link applySettle} is NOT its return: a settle does not schedule
 * an arrival the game would otherwise skip, it *is* the lap ending, and the
 * rider was always going to land. The difference shows in what refuses what —
 * `advance` let a survivor sit indefinitely at zero cost, whereas a rider that
 * sits on the belt occupies `conveyorCap` and gets shoved off by the next
 * dispatch that needs the room.
 */

// ── Legality / enumeration ─────────────────────────────────────────────────

/**
 * Canonical signature used to dedup interchangeable stack attackers when
 * enumerating legal dispatches from the stack.
 */
function attackerSig(a: SolverAttacker): string {
  const pairs = a.orbs.map(o => `${o.color}:${o.ammo}`).sort().join(',');
  const f = a.frozen ?? 0;
  return `${a.slotCount}|${pairs}|f=${f}|h=${a.isHammer ? 1 : 0}|m=${a.mystery ? 1 : 0}`;
}

// ── Reach-colors cache ─────────────────────────────────────────────────────

/**
 * Cache for `buildReachColors` results, keyed on a board-only Zobrist hash
 * (cells + barricade HP — the two inputs that actually affect `firstVisible`).
 * Many distinct (board, queue, conveyor, stack) tuples share the same board
 * content (board changes only when cubes clear or barricades break) so on
 * transposition-heavy searches this cache lets us skip the O(W*H)
 * perimeter-scan cost on the majority of states.
 *
 * Fields NOT in the key:
 *   - queue / conveyor / stack (obviously — these are the axis we want to
 *     share across).
 *   - door HP (doors are transparent regardless of HP — the door `doorCells`
 *     set in meta is immutable).
 *   - wall HP (walls don't affect `firstVisible` — they are consulted by
 *     `findBlockingWall` inside `simulateAtomicLap`, which is NOT
 *     `buildReachColors`).
 *   - mega HP (when mega HP > 0 the cells still carry the mega's color in
 *     the board array; when mega HP hits 0 the board cells get cleared. The
 *     board array already reflects the current visible state).
 *
 * Including barricade HP in the key is essential: barricades with HP>0 are
 * invisible to shooting (they `continue` past their cells in the scan loop),
 * so clearing a barricade changes which cubes are first-visible.
 */
export class ReachColorsCache {
  private readonly map = new Map<bigint, Map<number, number>>();
  hits = 0;
  misses = 0;
  private readonly capacity: number;

  constructor(capacity = 1_000_000) {
    this.capacity = capacity;
  }

  get(hash: bigint): Map<number, number> | undefined {
    const v = this.map.get(hash);
    if (v !== undefined) this.hits++;
    return v;
  }

  set(hash: bigint, value: Map<number, number>): void {
    this.misses++;
    if (this.map.size >= this.capacity) {
      // Simple overflow policy: drop everything. The rehydration cost is
      // ~60µs per miss; cheaper than LRU bookkeeping on every access.
      this.map.clear();
    }
    this.map.set(hash, value);
  }

  get size(): number {
    return this.map.size;
  }
}

/**
 * Cache for depth-N exposure scores, keyed on (stateHash, candidateAttackerId).
 *
 * When `getLegalActions` is called with zero-reach candidates and multiple
 * zero-reach dispatches are legal, each candidate is scored via a forward
 * simulation that dispatches the candidate, then (greedily) chains up to
 * `maxDepth` additional zero-reach dispatches looking for the first state
 * that offers a productive dispatch. The score is higher when productive
 * reach is unlocked SOONER (i.e., fewer chained zero-reach steps needed).
 *
 * This cache amortizes the simulation cost across repeated visits to the
 * same state. Critical because on occlusion-heavy cute levels the search
 * visits many transpositions of the same state shape, and the exposure
 * simulation (N=5 applyDispatch calls per candidate * 5-10 candidates)
 * would dominate per-decision cost without memoization.
 *
 * Created fresh per `search()` call and never shared across searches — the
 * exposure-score meaning is specific to the search's initial state (the
 * simulation uses the CURRENT board / queue content and is NOT invariant
 * across levels).
 */
export class ExposureScoreCache {
  private readonly map = new Map<bigint, Map<number, number>>();
  hits = 0;
  misses = 0;
  private readonly capacity: number;

  constructor(capacity = 500_000) {
    this.capacity = capacity;
  }

  get(stateHash: bigint, candidateId: number): number | undefined {
    const inner = this.map.get(stateHash);
    if (inner === undefined) return undefined;
    const v = inner.get(candidateId);
    if (v !== undefined) this.hits++;
    return v;
  }

  set(stateHash: bigint, candidateId: number, score: number): void {
    this.misses++;
    if (this.map.size >= this.capacity) {
      // Simple overflow policy: drop everything. The rehydration cost is
      // O(N * getLegalActions) per miss; cheaper than LRU bookkeeping.
      this.map.clear();
    }
    let inner = this.map.get(stateHash);
    if (inner === undefined) {
      inner = new Map();
      this.map.set(stateHash, inner);
    }
    inner.set(candidateId, score);
  }

  get size(): number {
    return this.map.size;
  }
}

/**
 * Zobrist hash over only the board-visible state (cells + barricade HP).
 * Same primitives as {@link hashState}, but excludes everything that
 * doesn't feed `firstVisible`.
 *
 * Used as the key for {@link ReachColorsCache}. Two states that differ
 * only in queue / conveyor / stack / door HP / wall HP / mega HP produce
 * the same board hash and share a cache entry.
 *
 * This is an O(N) full scan over the board. The solver maintains an
 * incremental `boardHash` value on each `SolverState` so that hot-path
 * callers pay O(1) per state instead of repeating this scan. Prefer
 * `state.boardHash` on the search hot path; this function is kept for
 * initial-state construction and for tests / debugging.
 */
export function hashBoardOnly(state: SolverState): bigint {
  let h = 0n;
  const board = state.board;
  for (let i = 0; i < board.length; i++) {
    const v = board[i];
    if (v !== 0) h ^= getCellColorHash(i, v);
  }
  for (const cellStr of Object.keys(state.barricadeHp)) {
    const cell = Number(cellStr);
    const hp = state.barricadeHp[cell];
    if (hp > 0) h ^= mixHp(getBarricadeSalt(cell), hp);
  }
  return h;
}

/**
 * Context for depth-N exposure scoring. When provided to `getLegalActions`,
 * zero-reach candidates are ranked by simulating forward up to
 * `EXPOSURE_MAX_DEPTH` dispatches, preferring candidates that unlock
 * productive reach soonest.
 *
 * All fields required for the simulation:
 *   - `hash`: current state's Zobrist hash (for `applyDispatch`).
 *   - `exposureCache`: memoization of (stateHash, candidateId) → score.
 *   - `firstVisibleCache`: shared `firstVisible` cache (passed to `applyDispatch`).
 *
 * When omitted, `getLegalActions` falls back to the original O(lanes)
 * depth-1 exposure score (no forward simulation). Inner recursive calls
 * from the simulator itself pass `undefined` to avoid infinite recursion.
 */
export interface ExposureLookaheadCtx {
  hash: bigint;
  exposureCache: ExposureScoreCache;
  firstVisibleCache?: FirstVisibleCache;
}

/**
 * Scoring context for the C1 multi-ply opening heuristic. When provided to
 * `getLegalActions`, the top-K productive candidates are re-ranked using
 * `scoreCandidate(action)` (higher is better). Spec § 3.5.
 *
 * Hot-path guards: re-rank is SKIPPED when (a) there is ≤ 1 productive
 * candidate, or (b) `phase.kind === 'PRE_WIN'`.
 *
 * `phase` is opaque from the simulator's perspective — the simulator
 * doesn't know about phases, only `scoreCandidate` does. Passing it
 * through gives the simulator enough information to apply the PRE_WIN
 * hot-path guard without importing PhaseIdentifier (which would be a
 * cycle: PhaseIdentifier already depends on AtomicSimulator).
 */
export interface C1ScoringContext {
  /**
   * Current phase from PhaseIdentifier. Typed structurally as `{ kind: string }`
   * to avoid the import cycle (PhaseIdentifier already depends on AtomicSimulator).
   * The simulator only inspects equality with `'PRE_WIN'` for the hot-path guard;
   * see `Phase` in PhaseIdentifier.ts for the full discriminated union.
   */
  phase: { kind: string };
  /** Width of the candidate window to re-rank. Default callers pass 8. */
  topK: number;
  /** Score one action. Higher = preferred. */
  scoreCandidate: (action: SolverAction) => number;
}

/**
 * Depth limit for the chained zero-reach exposure simulator. See
 * {@link ExposureLookaheadCtx} and the `exposureScoreDeep` helper inside
 * `getLegalActions`.
 *
 * Rationale: cute levels (lv1374, lv695) show winning human play that
 * chains 2-3 zero-reach dispatches in a row before the first productive
 * dispatch appears. N=5 gives us headroom for those chains plus a
 * safety margin. Per-candidate cost is O(N * (getLegalActions + applyDispatch))
 * ~ O(N * 100µs) so 5 candidates * 5 steps ~ 2.5ms per decision — acceptable
 * on the ~60s tier-2 budget.
 */
const EXPOSURE_MAX_DEPTH = 5;

/**
 * Enumerate legal moves at `state` under ordered flow: every dispatch, plus
 * `settle` whenever the in-flight ring is non-empty. Deterministic ordering:
 *   1. Dispatches likely to deplete the attacker this lap (heuristic via reach).
 *   2. Remaining queue-front dispatches.
 *   3. Remaining unique stack dispatches.
 *   4. `settle` — clears no cubes, but converts a rider into a dispatchable
 *      stack attacker; ranked above a wasted zero-reach dispatch.
 *   5. Zero-reach dispatches (tried last).
 *
 * QUIESCENCE DRAIN falls out of this for free: when no dispatch is legal and the
 * ring is loaded, `settle` is the only action, so the search drains the ring
 * FIFO exactly as §3 of the semantics doc requires — and an empty result here
 * therefore means "no dispatch AND nothing in flight", the genuine dead end.
 *
 * Dedup rules:
 *   - Stack: two attackers with the same canonical signature produce only one
 *     dispatch action. Also: among same-color single-orb stack attackers,
 *     dispatching a LOWER-ammo one dominates higher-ammo (it's weaker but
 *     preserves ammo elsewhere) — we prune the higher-ammo variant.
 *
 * @param state  The current solver state.
 * @param reachCache Optional cache for `buildReachColors` results. When
 *                   provided, reach-colors computations are memoized across
 *                   states that share the same board content (massive speedup
 *                   on transposition-heavy searches where the board is static
 *                   while queue/conveyor/stack vary).
 * @param lookaheadCtx Optional context enabling depth-N exposure scoring for
 *                     zero-reach candidates. When provided, chained zero-reach
 *                     openings (peel 2-3 queue layers before finding productive
 *                     reach) are ranked via forward simulation. When omitted,
 *                     falls back to depth-1 O(lanes) heuristic. Inner calls
 *                     from the simulator pass `undefined` to avoid recursion.
 */
export function getLegalActions(
  state: SolverState,
  reachCache?: ReachColorsCache,
  lookaheadCtx?: ExposureLookaheadCtx,
  scoringContext?: C1ScoringContext,
): SolverAction[] {
  const out: SolverAction[] = [];
  const seenSig = new Set<string>();
  const seenSingleColorLowest = new Map<number, number>(); // color → lowest ammo seen

  // Belt capacity gates DISPATCH: an attacker is a candidate only when the
  // ring has room for its slotCount right now (`ringUsed + slotCount <=
  // conveyorCap`). This is the live game's tap-refusal rule — the burst model,
  // where occupancy never refused and a full belt forced arrivals instead, is
  // retired (Arthur, 2026-09-03). `slotCount > conveyorCap` (too wide to ride
  // at all) is subsumed by the same comparison whenever the ring is empty and
  // still checked structurally by `applyDispatch`.
  const conveyorCap = state.conveyorCap;
  const ringUsed = conveyorSlotsUsed(state);
  const numLanes = state.queueLanes.length;

  // Queue fronts first: collect consensus-passing, non-frozen, non-hammer.
  //
  // Consensus check fast paths:
  //   - Single-orb (slotCount === 1): CAN'T span multiple lanes (see
  //     `packQueueLanes` in SolverState.ts — slotCount N always occupies
  //     exactly N consecutive lanes). Any single-orb attacker we get from
  //     `lane[0]` is trivially at position 0 in its sole lane. No scan
  //     needed; consensus is vacuous.
  //   - Multi-orb (slotCount > 1): the attacker occupies exactly
  //     `slotCount` CONSECUTIVE lanes starting at some lane L. If we're
  //     looking at `queueLanes[l][0]`, either this IS the home lane
  //     (attacker occupies l, l+1, ..., l+slotCount-1) in which case we
  //     must verify it sits at position 0 in each of those lanes, OR
  //     this is NOT the home lane (some previous lane had this attacker
  //     higher up, so dispatching from here would be out of consensus).
  //     Either way the check reduces to a DIRECT reference comparison at
  //     O(slotCount) lanes, no indexOf scan at all.
  //
  // On cute levels (numLanes=5, per-lane depth ~500) the previous code
  // ran `lane.indexOf(front)` five times — four of which walked the
  // entire 500-element lane before returning -1, costing ~2 kOps per
  // front per getLegalActions call. The new check is ~5 pointer compares.
  //
  // Soundness: the consensus invariant from `packQueueLanes` guarantees
  // that if an attacker appears at position 0 in every lane it occupies,
  // it necessarily occupies a contiguous slotCount-wide run of lanes, and
  // if it does NOT pass consensus, it either (a) sits behind another
  // attacker in one of its lanes (pos > 0 somewhere — caught by the
  // direct index comparison in the check below) or (b) is not yet at
  // lane[0] in any of its home lanes (also caught). Mirror semantics
  // to the prior indexOf scan — we've verified this against the full
  // AtomicSimulator + Search test suite.
  const queueCandidates: SolverAttacker[] = [];
  const queueCandidateIds = new Set<number>();
  const passesMultiLaneConsensus = (front: SolverAttacker, l: number): boolean => {
    const sc = front.slotCount;
    if (sc <= 1) return true;
    // Multi-orb: must be at position 0 in every lane it occupies. A
    // multi-orb is placed at contiguous lanes h..h+sc-1 (see
    // `packQueueLanes`); the fullyDepleted/frozen-thaw branches in
    // `applyDispatch`'s depleted branch preserves identity across lanes
    // via an id-keyed copy map, so a multi-orb is always at the same
    // position in all of its lanes. Thus: if `front` is at pos 0 in
    // lane `l`, consensus holds iff it's ALSO at pos 0 in lanes l+1,
    // ..., l+sc-1 — a direct reference check, no indexOf needed.
    // Absent-from-lane (e.g., `queueLanes[l+k][0] !== front`) also
    // rejects — that's an invariant violation and should fail consensus.
    for (let k = 1; k < sc; k++) {
      if (state.queueLanes[l + k]?.[0] !== front) return false;
    }
    return true;
  };
  for (let l = 0; l < numLanes; l++) {
    const front = state.queueLanes[l][0];
    if (!front) continue;
    if (front.isHammer) continue;
    if ((front.frozen ?? 0) > 0) continue;
    if (ringUsed + front.slotCount > conveyorCap) continue; // no belt room = no dispatch
    if (queueCandidateIds.has(front.id)) continue;
    if (!passesMultiLaneConsensus(front, l)) continue;
    queueCandidateIds.add(front.id);
    queueCandidates.push(front);
  }

  // Stack candidates: one per canonical signature; same-color single-orb
  // dominance prune = keep lowest ammo only WHEN reach is scarce. When
  // reach >= total same-color stack ammo, all single-orb siblings are
  // enumerated (C2 — see docs/superpowers/specs/2026-05-07-multi-ply-
  // opening-heuristic-design.md § 4).
  //
  // Two-pass: first pass tallies per-color total stack ammo across single-orb
  // attackers (after the per-color signature dedup so we don't double-count
  // identical canonical entries). Second pass applies the dominance rule
  // CONDITIONALLY on totalReach[c] < totalStackAmmo[c]. The reach map is
  // built lazily and only when there is at least one duplicate-color
  // single-orb candidate to even consider relaxing.
  const stackCandidates: SolverAttacker[] = [];
  // First pass: tally same-color stack ammo for the relaxation decision,
  // and record the canonical-deduped attackers. We DON'T apply the
  // dominance prune yet — we want to know totalStackAmmo first.
  const stackPreScreened: SolverAttacker[] = [];
  const totalStackAmmoByColor = new Map<number, number>();
  for (const a of state.stack) {
    if (a.isHammer) continue;
    if ((a.frozen ?? 0) > 0) continue;
    if (ringUsed + a.slotCount > conveyorCap) continue; // no belt room = no dispatch
    const sig = attackerSig(a);
    if (seenSig.has(sig)) continue;
    seenSig.add(sig);
    stackPreScreened.push(a);
    if (a.orbs.length === 1) {
      const c = a.orbs[0].color;
      totalStackAmmoByColor.set(
        c,
        (totalStackAmmoByColor.get(c) ?? 0) + a.orbs[0].ammo,
      );
    }
  }
  // Lazily build/reuse the reach map (only if we actually have a duplicate
  // single-orb color in the stack — otherwise dominance never fires and
  // we can skip the perimeter scan entirely).
  let stackReach: Map<number, number> | null = null;
  const ensureStackReach = (): Map<number, number> => {
    if (stackReach !== null) return stackReach;
    if (reachCache !== undefined) {
      const cached = reachCache.get(state.boardHash);
      if (cached !== undefined) {
        stackReach = cached;
        return stackReach;
      }
      const r = buildReachColors(state);
      reachCache.set(state.boardHash, r);
      stackReach = r;
      return stackReach;
    }
    stackReach = buildReachColors(state);
    return stackReach;
  };
  // Second pass: emit candidates, applying the conditional lowest-ammo
  // dominance prune.
  for (const a of stackPreScreened) {
    if (a.orbs.length === 1) {
      const c = a.orbs[0].color;
      const totalAmmo = totalStackAmmoByColor.get(c) ?? a.orbs[0].ammo;
      // Only consult reach when there's a duplicate-color group at all.
      // A singleton color (totalAmmo equals this attacker's ammo and no
      // other entry in stackPreScreened shares the color) cannot trigger
      // the prune.
      const hasDuplicateColor = totalAmmo > a.orbs[0].ammo;
      if (hasDuplicateColor) {
        const reach = ensureStackReach();
        const reachForColor = reach.get(c) ?? 0;
        const reachAbundant = reachForColor >= totalAmmo;
        if (!reachAbundant) {
          // Apply original lowest-ammo dominance.
          const prev = seenSingleColorLowest.get(c);
          if (prev === undefined) {
            seenSingleColorLowest.set(c, a.orbs[0].ammo);
          } else if (a.orbs[0].ammo < prev) {
            for (let i = stackCandidates.length - 1; i >= 0; i--) {
              const x = stackCandidates[i];
              if (x.orbs.length === 1 && x.orbs[0].color === c && x.orbs[0].ammo > a.orbs[0].ammo) {
                stackCandidates.splice(i, 1);
              }
            }
            seenSingleColorLowest.set(c, a.orbs[0].ammo);
          } else if (a.orbs[0].ammo > prev) {
            // Higher-ammo dominated under scarce reach, skip.
            continue;
          }
        }
        // reachAbundant === true → push all siblings (no dominance).
      }
      // hasDuplicateColor === false → push (single occurrence, dominance
      // is vacuous).
    }
    stackCandidates.push(a);
  }

  // Rank by "likely to deplete this lap" (crude reach-check). Skip the entire
  // ranking step when there's at most one candidate to rank: with 0 or 1
  // candidates, the deplete/other split is meaningless and the caller's
  // ordering is unchanged either way — it saves a `buildReachColors` (4*W
  // invocations of `firstVisible`, scanning entire rows/cols on a 128x128
  // board) on every state that has nothing to choose between. Under the old
  // parking model these were the states where the conveyor had saturated and
  // only `advance` was legal; now they are states with a single dispatchable
  // attacker, which is just as common late in a level.
  const totalCandidates = queueCandidates.length + stackCandidates.length;
  if (totalCandidates <= 1) {
    for (const a of queueCandidates) out.push({ type: 'dispatch', attackerId: a.id });
    for (const a of stackCandidates) out.push({ type: 'dispatch', attackerId: a.id });
    if (state.conveyor.length > 0) out.push({ type: 'settle' });
  } else {
    // Reach-colors cache lookup: board content drives this result, so on
    // transposition-heavy searches (where the same board is revisited with
    // different queue/conveyor/stack permutations) we skip the O(4*W)
    // firstVisible sweep entirely. Key is `state.boardHash` — maintained
    // incrementally by every board/barricade mutation, so lookup is O(1).
    let reach: Map<number, number>;
    if (reachCache !== undefined) {
      const cached = reachCache.get(state.boardHash);
      if (cached !== undefined) {
        reach = cached;
      } else {
        reach = buildReachColors(state);
        reachCache.set(state.boardHash, reach);
      }
    } else {
      reach = buildReachColors(state);
    }
    const isDepleteThisLap = (a: SolverAttacker): boolean => {
      // Attacker depletes if every remaining orb finds a reachable same-color line
      // at least `ammo` times. This is a lower bound (doesn't account for orb
      // interplay). Still useful as a dispatch-priority heuristic.
      for (const orb of a.orbs) {
        if (orb.ammo <= 0) continue;
        if ((reach.get(orb.color) ?? 0) < orb.ammo) return false;
      }
      return true;
    };

    /**
     * Does any live orb on this attacker have at least one matching firing
     * line right now? An attacker with zero reach cannot fire a single shot
     * this lap — its dispatch is a no-op on the board, and just moves the
     * attacker around between queue/conveyor/stack.
     *
     * Cute levels spend 70%+ of their expansions on zero-reach dispatches
     * (see probe-churning.ts on level 1794): the conveyor and queue fronts
     * are often full of attackers whose colors have no first-visible match
     * on the current board, yet the search still expands each dispatch as
     * a DFS child.
     */
    const isZeroReach = (a: SolverAttacker): boolean => {
      for (const orb of a.orbs) {
        if (orb.ammo <= 0) continue;
        if ((reach.get(orb.color) ?? 0) > 0) return false;
      }
      return true;
    };

    /**
     * Heuristic score for a dispatch candidate: higher = try first.
     *
     * Intuition: we want DFS to explore moves that (a) clear the most
     * cubes this lap, and (b) leave the least leftover ammo on the
     * attacker (to minimise future stack pressure).
     *
     * Score = sum over live orbs of:
     *    min(ammo, reach[color])    // cubes cleared
     *  - max(0, ammo - reach[color]) // leftover ammo (penalty)
     *
     * A depleting attacker (ammo ≤ reach) gets score = ammo (no
     * penalty). A partially-productive attacker (ammo > reach) gets
     * score = 2*reach - ammo (could be negative for severe overflows).
     *
     * The penalty term is what distinguishes this from a pure
     * clearance heuristic and is the critical fix for stack-grinding
     * regimes on cute levels: a high-ammo attacker against a low-reach
     * board gets a LOW score (often negative), deprioritising it below
     * any low-ammo alternative that would deplete cleanly.
     *
     * No soundness impact: all branches are still enumerated within
     * each tier; only their order shifts.
     */
    const estScore = (a: SolverAttacker): number => {
      let score = 0;
      for (const orb of a.orbs) {
        if (orb.ammo <= 0) continue;
        const r = reach.get(orb.color) ?? 0;
        const fires = r < orb.ammo ? r : orb.ammo;
        const leftover = orb.ammo - fires; // max(0, ammo - reach) since reach >= 0
        score += fires - leftover;
      }
      return score;
    };

    // Split each source into three tiers:
    //   depletes   — productive, likely to deplete this lap (try first).
    //   productive — productive, fires >=1 shot but not fully (try next).
    //   zero       — fires 0 shots on this board (try LAST; may be pruned).
    const queueDepletes: SolverAttacker[] = [];
    const queueProductive: SolverAttacker[] = [];
    const queueZero: SolverAttacker[] = [];
    for (const a of queueCandidates) {
      if (isZeroReach(a)) queueZero.push(a);
      else if (isDepleteThisLap(a)) queueDepletes.push(a);
      else queueProductive.push(a);
    }
    const stackDepletes: SolverAttacker[] = [];
    const stackProductive: SolverAttacker[] = [];
    const stackZero: SolverAttacker[] = [];
    for (const a of stackCandidates) {
      if (isZeroReach(a)) stackZero.push(a);
      else if (isDepleteThisLap(a)) stackDepletes.push(a);
      else stackProductive.push(a);
    }

    // Within each productivity tier, order by heuristic score descending
    // (clearance - leftover-ammo). DFS tries the highest-impact +
    // least-stack-pressure dispatch first, which on deep puzzles (cute
    // levels with 60+ attackers and 3K+ cubes) typically leads to the
    // winning branch orders of magnitude faster. The penalty for leftover
    // ammo is the critical piece for cute levels: without it, the solver
    // happily piles same-color attackers onto the stack until overflow,
    // wasting huge chunks of DFS work in guaranteed-LOSS subtrees.
    //
    // The sort is stable (array .sort) and uses a simple descending-
    // score comparator; identity tie-break preserves the prior in-tier
    // order (queue-lane order / stack-canonical order) when two
    // attackers have identical score.
    const scoreDesc = (x: SolverAttacker, y: SolverAttacker): number =>
      estScore(y) - estScore(x);
    queueDepletes.sort(scoreDesc);
    queueProductive.sort(scoreDesc);
    stackDepletes.sort(scoreDesc);
    stackProductive.sort(scoreDesc);

    // Zero-reach dispatches are DEPRIORITIZED, not pruned.
    //
    // History (correctness fix, 2026-04):
    // Previously, when a productive dispatch existed and no walls /
    // barricades / hammers could be affected, we dropped all zero-reach
    // candidates under a "dominance" argument: any WIN starting with a
    // zero-reach dispatch should be rewritable to postpone that dispatch
    // until after a productive one. This is UNSOUND on occlusion-heavy
    // cute levels. Human witnesses on lv695 / lv1374 (known-human-winning
    // replays) show that 16-26% of the human's chosen dispatches are
    // zero-reach moves whose purpose is to PEEL QUEUE LAYERS — shuffling
    // the queue fronts to expose deeper attackers whose reach IS needed
    // to clear the board. The "postpone until after a productive" rewrite
    // doesn't work there because the productive dispatch the rewrite
    // would use may itself depend on a queue shape the zero-reach move
    // just changed; peeling reveals attackers the productive path never
    // would. Running `scripts/archive/solver-diag/diagnose-zero-reach-prune.ts` against the
    // lv695 witness reports 46/174 winning dispatches that would have
    // been eliminated by the prune (26.4%); lv1374 reports 15/90 (16.7%).
    //
    // Current behaviour: zero-reach candidates are appended AFTER
    // productive candidates, so DFS still tries them last. The ordering benefit (solver rarely has to explore zero-reach
    // subtrees on easy levels because the productive-first path wins
    // quickly and TT shortcircuits the rest) is preserved. On hard levels
    // where the zero-reach move is actually required, the candidate is
    // still in the search space and will be explored on backtrack.

    // ── C1 multi-ply re-rank (hot-path guarded) ────────────────────────
    //
    // When a scoringContext is provided, re-rank the top-K productive
    // candidates by the caller's score. Hot-path guards: skip if there
    // is ≤ 1 productive candidate or the phase is PRE_WIN (the existing
    // clearance scorer already handles PRE_WIN optimally).
    if (
      scoringContext !== undefined
      && scoringContext.phase.kind !== 'PRE_WIN'
    ) {
      const productive: SolverAttacker[] = [];
      productive.push(...queueDepletes);
      productive.push(...stackDepletes);
      productive.push(...queueProductive);
      productive.push(...stackProductive);
      // Guard: re-rank requires a positive window. A non-positive topK
      // disables C1 ordering and falls through to the original four-tier
      // emit order below.
      if (productive.length > 1 && scoringContext.topK > 0) {
        // Build (action, score) pairs for the top-K productive entries.
        const K = Math.min(scoringContext.topK, productive.length);
        const window = productive.slice(0, K);
        const scored = window.map(a => ({
          a,
          score: scoringContext.scoreCandidate({
            type: 'dispatch',
            attackerId: a.id,
          }),
        }));
        scored.sort((x, y) => y.score - x.score);
        // Splice the re-ranked window back into productive[0..K).
        for (let i = 0; i < K; i++) productive[i] = scored[i].a;
        // Repartition back into the four tier buckets, preserving the
        // re-ranked order across them. We rebuild queue/stack tiers as
        // a single flat list so the "deplete first" preference is
        // overruled by the caller's score; the four-tier ordering was
        // a heuristic, the C1 score is more informed.
        queueDepletes.length = 0;
        stackDepletes.length = 0;
        queueProductive.length = 0;
        stackProductive.length = 0;
        const queueIds = new Set<number>();
        for (const lane of state.queueLanes) {
          const f = lane[0];
          if (f !== undefined) queueIds.add(f.id);
        }
        for (const a of productive) {
          const onQueue = queueIds.has(a.id);
          // We flatten the four-tier order into two buckets (queueProductive
          // and stackProductive). The C1 score below now drives ordering,
          // intentionally overruling the deplete-first preference — see the
          // rationale in the comment block above (the win-bonus dominates,
          // gating-aware reach trumps short-term clearance). The downstream
          // emit order (queueDepletes → stackDepletes → queueProductive
          // → stackProductive → queueZero → stackZero) becomes
          // queueProductive (pre-ranked) → stackProductive (pre-ranked)
          // → queueZero → stackZero, putting C1-ranked productives ahead of
          // any other dispatch.
          if (onQueue) queueProductive.push(a);
          else stackProductive.push(a);
        }
      }
    }

    for (const a of queueDepletes) out.push({ type: 'dispatch', attackerId: a.id });
    for (const a of stackDepletes) out.push({ type: 'dispatch', attackerId: a.id });
    for (const a of queueProductive) out.push({ type: 'dispatch', attackerId: a.id });
    for (const a of stackProductive) out.push({ type: 'dispatch', attackerId: a.id });

    // ── `settle`, ranked between the productive and the zero-reach tiers ──
    //
    // Placement rationale. A settle clears no cubes, so it never belongs above
    // a productive dispatch. But it beats a zero-reach dispatch on two counts:
    // it converts a ring rider into a STACK attacker (which may be productive
    // next ply, and is the only way to re-dispatch a rider at all), and it
    // consumes nothing from the queue. This is also exactly where the retired
    // `advance` used to sit, so the move-ordering shape the tier heuristics
    // were tuned against is preserved.
    //
    // NEVER PRUNED, only deprioritised — and under the age-bounded rules a
    // settle is even less removable than under the burst model: it is the only
    // voluntary way to free belt room (dispatch refuses without it), and every
    // settle also spends one action of every other rider's grace window, so
    // "when to settle" is a genuine resource decision the search must own.
    // Soundness first.
    if (state.conveyor.length > 0) out.push({ type: 'settle' });

    // Zero-reach dispatch ordering: rank by "exposure" — how quickly a
    // productive dispatch becomes available along a chain of zero-reach
    // peels from here.
    //
    // Motivation (lv250 fruits-01-01, 4705 cubes, cute-level regime):
    // The board's perimeter shows ONLY color 2 (the fruit outline). The
    // root has 2 productive color-2 dispatches (ids 3 and 5); dispatching
    // both exposes a state where ALL 5 queue fronts are color 0 (red,
    // zero-reach). In that state the solver must
    // dispatch SOME zero-reach attacker to shuffle the queue and expose
    // fresh fronts. Which zero-reach attacker matters:
    //   - id=1 -> exposes id=6 (color 2 productive) ✓
    //   - id=4 -> exposes id=9 (color 2 productive) ✓
    //   - id=8 -> exposes id=13 (color 0 zero-reach) ✗ (wasted turn)
    //
    // Without ordering, DFS explores in queue-lane order (id=1, 2, 8, 4, 10)
    // and spends significant time in the id=8 / id=10 subtrees (which
    // chain more zero-reach before finding productive) before backtracking
    // to try id=4. With this ordering, DFS tries the "opens productive
    // doors" dispatches first, finds the winning branch orders of
    // magnitude faster.
    //
    // Two scoring modes:
    //
    //   (a) Shallow (depth-1) — used when `lookaheadCtx` is NOT provided.
    //       For a queue-zero candidate Z in lane L at position 0, look at
    //       the attacker at position 1 in L: does it become consensus-
    //       eligible post-dispatch AND have reach > 0? Sum reach counts
    //       across all such exposed successor-fronts. Cheap: O(lanes²)
    //       per candidate, reuses already-computed `reach` map.
    //
    //   (b) Deep (depth-N) — used when `lookaheadCtx` IS provided. For
    //       each zero-reach candidate Z, simulate forward: dispatch Z, then
    //       up to `EXPOSURE_MAX_DEPTH-1` additional greedy zero-reach
    //       chains looking for a state that offers productive reach. Score
    //       is `(maxDepth - stepWhereProductiveAppeared) * 1000 + reachAtThatStep`,
    //       so candidates that unlock productive reach sooner win. If no
    //       productive is found within N steps, the depth-1 shallow score
    //       is returned as a tiebreaker (preserves prior behaviour).
    //
    // Mode (b) is critical for hard cute levels (lv1374, lv695) where the
    // winning play requires peeling 2-3 zero-reach queue layers BEFORE the
    // first productive dispatch appears — at depth-1 all such candidates
    // look equally bad (score = 0) and DFS falls back to queue-lane order.
    //
    // Stack zero-reach candidates don't shuffle the queue, so their
    // score is based purely on the estScore (typically negative for
    // full-ammo single-color zero-reach attackers); we keep them in
    // their canonical order behind queue zeros.
    //
    // Soundness: no-op in terms of legal moves; just reorders.
    if (queueZero.length > 1) {
      // Depth-1 (shallow) scorer — used as fallback inside the deep scorer
      // to pick the "next" zero-reach to chain during simulation, and
      // as the primary scorer when no lookahead context is provided.
      const exposureScoreShallow = (
        zState: SolverState,
        z: SolverAttacker,
        zReach: Map<number, number>,
      ): number => {
        // For each lane containing z, look at the attacker at position 1.
        // If that attacker would become consensus-eligible after z is
        // dispatched (currently at position 0 in all OTHER lanes it
        // occupies, or about to shift up to pos 0 thanks to z being
        // removed from a shared lane), and has reach > 0, count its
        // reach as an exposed productive.
        //
        // Multi-lane invariant: a multi-orb attacker always occupies a
        // CONTIGUOUS run of `slotCount` lanes starting at some home lane
        // h — see `packQueueLanes` in SolverState.ts. Identity is
        // preserved across depletion/thaw, so a multi-orb sits at the
        // SAME position in every lane it occupies. That lets us skip
        // all `indexOf` scans and use direct reference compares on a
        // tiny contiguous lane block.
        //
        // For a shallow peel of z → consensus-eligible nextFront, the
        // invariants combine into a simple rule: `nextFront` becomes
        // consensus-eligible iff either
        //   (a) nextFront is single-orb (it only lives in the lane
        //       where z was; lane[1] → lane[0] post-dispatch); or
        //   (b) nextFront is multi-orb (slotCount nfSc) and its
        //       contiguous home block [H..H+nfSc-1] fits inside z's
        //       home block [zHome..zHome+zSc-1]. Because nextFront's
        //       per-lane position is uniform (say `nfPos`) and we
        //       saw nfPos=1 in `lane[laneIdx][1]`, the only way for
        //       nextFront to land at pos 0 in ALL its lanes post-
        //       dispatch is for it to share every lane with z. Outside
        //       z's footprint nextFront would still be at nfPos=1,
        //       which is not 0 → consensus fails.
        const zNumLanes = zState.queueLanes.length;
        const zSc = z.slotCount;
        let score = 0;
        // Discover z's home lane: the first lane with z at pos 0.
        let zHome = -1;
        for (let l = 0; l < zNumLanes; l++) {
          if (zState.queueLanes[l][0] === z) { zHome = l; break; }
        }
        if (zHome === -1) return 0;

        const seen = new Set<number>();
        for (let offset = 0; offset < zSc; offset++) {
          const laneIdx = zHome + offset;
          const lane = zState.queueLanes[laneIdx];
          const nextFront = lane[1];
          if (!nextFront) continue;
          if (seen.has(nextFront.id)) continue;
          seen.add(nextFront.id);
          if (nextFront.isHammer) continue;
          if ((nextFront.frozen ?? 0) > 0) continue;
          const nfSc = nextFront.slotCount;
          if (nfSc > 1) {
            // Multi-orb nextFront: its contiguous home block must lie
            // entirely within z's home block. We know nextFront sits
            // at pos 1 of laneIdx; its home H is at most `laneIdx` and
            // at least `laneIdx - nfSc + 1`. The simplest correct
            // check is to probe each valid H and verify nextFront is
            // at pos 1 across H..H+nfSc-1, AND the whole block is
            // inside zHome..zHome+zSc-1.
            let nfInsideZ = false;
            for (let H = Math.max(zHome, laneIdx - nfSc + 1); H <= laneIdx; H++) {
              if (H + nfSc > zHome + zSc) break;
              let ok = true;
              for (let k = 0; k < nfSc; k++) {
                if (zState.queueLanes[H + k][1] !== nextFront) { ok = false; break; }
              }
              if (ok) { nfInsideZ = true; break; }
            }
            if (!nfInsideZ) continue;
          }
          // Sum reach across nextFront's live orbs.
          for (const orb of nextFront.orbs) {
            if (orb.ammo <= 0) continue;
            score += zReach.get(orb.color) ?? 0;
          }
        }
        return score;
      };

      const zScore = new Map<number, number>();

      if (lookaheadCtx !== undefined) {
        // Deep (depth-N) scorer. Simulates the queue-peel chain forward
        // and rewards candidates that reveal productive reach soonest.
        //
        // Per-candidate cost: O(N * (getLegalActions + applyDispatch)).
        // With N=EXPOSURE_MAX_DEPTH=5, 5-10 candidates per decision, and
        // ~100µs per getLegalActions+applyDispatch pair, total ~5ms per
        // decision. Memoized via `exposureCache` so repeated visits of
        // the same state (transposition-heavy searches) amortize.
        const { hash: rootHash, exposureCache, firstVisibleCache } = lookaheadCtx;

        const deepScoreFor = (candidate: SolverAttacker): number => {
          // Cache hit?
          const cached = exposureCache.get(rootHash, candidate.id);
          if (cached !== undefined) return cached;

          // Simulate the initial dispatch of `candidate`.
          const initialStep = applyDispatch(
            state,
            rootHash,
            { type: 'dispatch', attackerId: candidate.id },
            firstVisibleCache,
          );
          if (initialStep === null) {
            // Shouldn't happen (candidate came from a legal-action pass)
            // but if it does, fall back to shallow depth-1.
            const fallback = exposureScoreShallow(state, candidate, reach);
            exposureCache.set(rootHash, candidate.id, fallback);
            return fallback;
          }

          let curState = initialStep.state;
          let curHash = initialStep.hash;

          // Walk up to `EXPOSURE_MAX_DEPTH` additional steps. At each
          // step: if a productive dispatch exists, return the bonus
          // (closer to now = higher score). Otherwise pick the
          // highest-shallow-score zero-reach candidate and continue.
          for (let step = 1; step <= EXPOSURE_MAX_DEPTH; step++) {
            // Terminal check: empty board, empty queue+conveyor+stack,
            // etc. If the chain reaches a terminal state, we didn't
            // find a productive dispatch — treat as "no useful peel".
            // `getLegalActions` on a terminal returns []; we handle that
            // by returning 0 (which matches the "no productive found"
            // branch below).
            const innerReach = reachCache !== undefined
              ? (reachCache.get(curState.boardHash) ?? (() => {
                  const r = buildReachColors(curState);
                  reachCache.set(curState.boardHash, r);
                  return r;
                })())
              : buildReachColors(curState);

            // Classify the current-state candidates WITHOUT re-entering
            // the depth-N path (pass no lookaheadCtx).
            const innerActions = getLegalActions(curState, reachCache);
            if (innerActions.length === 0) {
              // Terminal — no productive, no zero-reach to chain.
              exposureCache.set(rootHash, candidate.id, 0);
              return 0;
            }

            // Build a cheap attacker-by-id lookup ONCE per step iteration.
            // The innerActions loop below queries every dispatch action's
            // attacker twice (productive pass + zero-reach pass), so we
            // want a single up-front scan rather than two per-action
            // linear walks over queue fronts + stack. Only built when we
            // reach this point (innerActions is non-empty and deep
            // scoring is active), so the common single-candidate early-
            // exit path at the top of `getLegalActions` never pays for it.
            const innerAttackerById = new Map<number, SolverAttacker>();
            for (const lane of curState.queueLanes) {
              const f = lane[0];
              if (f !== undefined) innerAttackerById.set(f.id, f);
            }
            for (const a of curState.stack) {
              if (!innerAttackerById.has(a.id)) innerAttackerById.set(a.id, a);
            }

            // Look for ANY productive dispatch. `getLegalActions` orders
            // productive dispatches before zero-reach ones; simplest is to
            // check each action's attacker for a reach-matching orb.
            let bestProductiveReach = 0;
            for (const act of innerActions) {
              if (act.type !== 'dispatch') continue;
              const att = innerAttackerById.get(act.attackerId);
              if (att === undefined) continue;
              // Is this dispatch productive?
              let prodReach = 0;
              for (const orb of att.orbs) {
                if (orb.ammo <= 0) continue;
                const r = innerReach.get(orb.color) ?? 0;
                if (r > 0) prodReach += Math.min(r, orb.ammo);
              }
              if (prodReach > bestProductiveReach) bestProductiveReach = prodReach;
            }

            if (bestProductiveReach > 0) {
              // Found productive reach at `step` steps into the chain.
              // Higher score = fewer chained zero-reach dispatches
              // needed. The `* 1000` ensures step-count dominates
              // the reach-magnitude tiebreaker.
              const score = (EXPOSURE_MAX_DEPTH - step + 1) * 1000 + bestProductiveReach;
              exposureCache.set(rootHash, candidate.id, score);
              return score;
            }

            // No productive reach yet. Pick the best depth-1 zero-reach
            // to chain. Iterate dispatch actions, keep only zero-reach,
            // pick the one with highest shallow exposureScore.
            let bestZr: SolverAttacker | null = null;
            let bestZrScore = -Infinity;
            for (const act of innerActions) {
              if (act.type !== 'dispatch') continue;
              const att = innerAttackerById.get(act.attackerId);
              if (att === undefined) continue;
              // Is this zero-reach?
              let zr = true;
              for (const orb of att.orbs) {
                if (orb.ammo <= 0) continue;
                if ((innerReach.get(orb.color) ?? 0) > 0) { zr = false; break; }
              }
              if (!zr) continue;
              const s = exposureScoreShallow(curState, att, innerReach);
              if (s > bestZrScore) {
                bestZrScore = s;
                bestZr = att;
              }
            }

            if (bestZr === null) {
              // No zero-reach to chain and no productive found — we've
              // hit a dead state. Return 0 (worst score), so this
              // candidate sorts to the back.
              exposureCache.set(rootHash, candidate.id, 0);
              return 0;
            }

            // Advance the simulation one step deeper.
            const nextStep = applyDispatch(
              curState,
              curHash,
              { type: 'dispatch', attackerId: bestZr.id },
              firstVisibleCache,
            );
            if (nextStep === null) {
              // applyDispatch refused (consensus lost after the previous
              // step, capacity drop, etc.). Treat as dead-end: return 0.
              exposureCache.set(rootHash, candidate.id, 0);
              return 0;
            }
            curState = nextStep.state;
            curHash = nextStep.hash;
          }

          // maxDepth exhausted without finding productive reach. Fall
          // back to the shallow depth-1 score so candidates still
          // differentiate at the tail.
          const fallback = exposureScoreShallow(state, candidate, reach);
          exposureCache.set(rootHash, candidate.id, fallback);
          return fallback;
        };

        for (const z of queueZero) zScore.set(z.id, deepScoreFor(z));
      } else {
        // Shallow (depth-1) scoring — default when no lookahead context.
        for (const z of queueZero) zScore.set(z.id, exposureScoreShallow(state, z, reach));
      }

      // Cache scores so the sort comparator doesn't re-evaluate per
      // comparison (O(n log n) comparator calls × per-call work is
      // too expensive on wide queues).
      queueZero.sort((x, y) => (zScore.get(y.id) ?? 0) - (zScore.get(x.id) ?? 0));
    }
    for (const a of queueZero) out.push({ type: 'dispatch', attackerId: a.id });
    for (const a of stackZero) out.push({ type: 'dispatch', attackerId: a.id });
  }

  // Force-dispatch fallback: if no regular actions exist and we're "stuck on
  // a frozen queue front" (stack empty, only frozen non-hammer fronts
  // available), emit one dispatch action per frozen front that has
  // queue consensus and capacity. See `isStuckOnFrozenFront` doc + the
  // matching `applyDispatch` code path. Mirrors
  // `StrategicSimulator.tryDispatch` (line ~666 in StrategicSimulator.ts).
  if (out.length === 0 && isStuckOnFrozenFront(state)) {
    const sigSeen = new Set<number>();
    for (let l = 0; l < state.queueLanes.length; l++) {
      const front = state.queueLanes[l][0];
      if (!front) continue;
      if (front.isHammer) continue;
      if ((front.frozen ?? 0) <= 0) continue;
      if (sigSeen.has(front.id)) continue;
      // Consensus: single-orb vacuous; multi-orb needs a direct-reference
      // walk over the contiguous home lanes (same shape as the main
      // consensus path above).
      const sc = front.slotCount;
      if (sc > 1) {
        let ok = true;
        for (let k = 1; k < sc; k++) {
          if (state.queueLanes[l + k]?.[0] !== front) { ok = false; break; }
        }
        if (!ok) continue;
      }
      if (front.slotCount > state.conveyorCap) continue;
      sigSeen.add(front.id);
      out.push({ type: 'dispatch', attackerId: front.id });
    }
  }

  return out;
}

/**
 * Count the number of firing lines whose first-visible target has a given
 * color. Used as a quick "this orb has >=N shots available" heuristic.
 */
export function buildReachColors(state: SolverState): Map<number, number> {
  const counts = new Map<number, number>();
  const addColor = (c: number | null) => {
    if (c === null) return;
    counts.set(c, (counts.get(c) ?? 0) + 1);
  };
  for (let r = 0; r < state.boardHeight; r++) {
    addColor(firstVisible(state, 'right', r)?.color ?? null);
    addColor(firstVisible(state, 'left', r)?.color ?? null);
  }
  for (let c = 0; c < state.boardWidth; c++) {
    addColor(firstVisible(state, 'down', c)?.color ?? null);
    addColor(firstVisible(state, 'up', c)?.color ?? null);
  }
  return counts;
}

/**
 * Board has no remaining cubes AND no remaining mega HP.
 * (Mega cells on the board only disappear when mega HP hits 0, so this is
 * redundant in practice, but belt-and-suspenders.)
 */
export function isWin(state: SolverState): boolean {
  for (let i = 0; i < state.board.length; i++) {
    if (state.board[i] !== 0) return false;
  }
  // Any remaining mega HP means there's still a mega to destroy even if its
  // board cells are cleared (shouldn't happen, but guard).
  for (const id of Object.keys(state.megaHp)) {
    if (state.megaHp[Number(id)] > 0) return false;
  }
  return true;
}

/**
 * A state is lost if:
 *   - it is not a win, AND
 *   - stack slot usage > stackCap (an arrival that did not fit), or there are
 *     no legal actions at all.
 *
 * "No legal actions" is the dead end of `docs/ordered-flow-semantics.md`: no
 * dispatch is legal AND the ring is empty (a loaded ring always offers a
 * `settle`, so the quiescence drain runs itself out before this can fire).
 *
 * WIN BEATS OVERFLOW. The win test comes first because the two can co-occur in
 * one step: a dispatch resolves a whole lap, so the shot that clears the last
 * cube and an arrival that does not fit can land in the same action. The live
 * game ends the moment the board empties. (An age-forced arrival happens
 * BEFORE the lap, so that particular collision resolves as a loss inside
 * `applyDispatch` — see its doc — but a settle-then-win ordering still reaches
 * here, and `Search`'s terminal check has always tested `isWin` before the
 * overflow shortcut.)
 */
export function isLoss(state: SolverState): boolean {
  if (isWin(state)) return false;
  if (stackSlotsUsed(state) > state.stackCap) return true;
  return getLegalActions(state).length === 0;
}

// Re-export canonicalizeStack for test convenience.
export { canonicalizeStack };
